// ---------------------------------------------------------------------------
// Creative Performance Dashboard (enabled and accessible per-brand).
//
// Renders ad-level performance from the external analytics API via the
// server-side proxy (/api/brands/:brandId/creative-performance/*) — the brand
// API key never reaches the browser. Sections follow §5.2 of the rebuild
// guide: global controls, health strip, scorecard, funnel, creative quadrant,
// best personas, persona table, daily trend, gallery. (Batch cohorts removed
// 2026-08-11 by user request.)
//
// Visual language follows the reference dashboard the user supplied
// (attached_assets/Screenshot_2026-08-06_at_7.53.* / 7.54.*): light neutral
// canvas, white rounded cards, violet (#7C3AED) accent, rounded-full pill
// controls, verdict-style stat tiles, and call badges.
//
// Metric rules: every ratio comes from summed counts (shared/
// creative-performance.ts) — never averaged per-ad ratios. Immature ads
// (spend < min-spend or impressions < 20× min-spend) render translucent and
// are excluded from rankings/aggregates.
//
// Persona-first layout (Task #2254): every brand gets a Persona-only view —
// top scorecard → persona breakdown → zoned quadrant → funnel → trend →
// gallery. E-commerce keeps its health strip before the scorecard; lead mode
// consolidates the selection count into its scorecard. Producer comparison is a per-brand opt-in
// (config.comparisonEnabled, admin Analytics tab) and, when enabled, is
// confined to the TOP-LEVEL section (producer pills + delta scorecard).
// Nothing from the quadrant downward ever compares producers. The funnel and
// trend share ONE persona drill-down selection; the quadrant highlights it
// and its bubbles open the full persona detail view.
// CTR is an all-click CTR (the feed has no video-view/outbound-click data).
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { creativeIterationExternalSeedHref } from "@/lib/creative-iterations";
import { useCreativePerformanceAdLink } from "@/hooks/use-creative-performance-ad-link";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip as RTooltip,
  ReferenceLine, ReferenceArea, ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import type { DateRange as DayPickerDateRange } from "react-day-picker";
import {
  AlertTriangle, ArrowLeft, ChevronRight, Gauge, Video as VideoIcon, ImageIcon, Layers,
  Snowflake, Flame, RefreshCw, Archive, UserRound, Tag, ClipboardList, CalendarDays, Megaphone,
  DollarSign, ShoppingCart, TrendingUp, Target, Eye, MousePointerClick, ShoppingBag, CreditCard,
  CheckCircle2, ArrowRight, ChevronUp, Users, Link2, Loader2, Sparkles, SlidersHorizontal,
} from "lucide-react";
import {
  type CreativeAdRow, type CreativeTrendPoint, type CreativeTier, CREATIVE_TIERS,
  aggregateAds, type CreativeAggregate, isMatureAd, impressionsGateFor, mergeTrendByDate, median,
  parseCreativePerformanceSettings, DEFAULT_MIN_SPEND,
  leadGenAggregate, type LeadSourceField,
  periodDelta,
  type ProducerGroup, PRODUCER_LABELS, deriveInhouseLabel, classifyProducer, splitAdsByProducer,
  COMPARISON_METRICS, producerDelta,
  type CreativeAdLink, type CreativeAdEnrichment, buildCreativeAttributionLookup,
  applyCreativeAttribution,
  buildCanonicalPersonaResolver, canonicalizeCreativePerformancePersonas,
  buildProductNameLookup, fillMissingProducts,
  type CreativeAngleDetail, buildAngleDetailLookup,
  personaVerdict, type PersonaVerdict, UNKNOWN_ANGLE,
  detectMissingDaySpans, formatMissingDaySpan,
  type CreativeFunnelResponse, type CreativeFunnelStep, type CreativeFunnelTotals,
  type CreativeFunnelStepKey, selectedCreativeFunnelStep, KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID,
  KAYA_PRIMARY_CONVERSION_STEP, type CreativeAdMatchCandidate, type CreativeAdMatchSuggestion,
  creativeAdDisplayUrls,
} from "@shared/creative-performance";
import type { Brand, Persona } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import { apiRequest } from "@/lib/queryClient";
import { ReportingDateRangeControl as DateRangeControl } from "@/components/reporting-date-range-control";
import { creativeFeedQueryOptions, useCreativeFeedScopeError, type CreativeSavedData } from "@/lib/creative-performance-snapshot";
import { CreativePerformanceRefresh } from "@/components/creative-performance-refresh";

// Resolve a dashboard persona label through the same conservative stored-name
// resolver that canonicalizes feed rows before grouping. This keeps the avatar
// lookup aligned with cards, filters, drill-downs, and trend selections.
type PersonaMatch = { name: string; imageUrl: string | null };
function buildPersonaLookup(personas: Persona[] | undefined): (label: string) => PersonaMatch | null {
  const byCanonicalName = new Map<string, PersonaMatch>();
  for (const p of personas ?? []) {
    if (!p.name) continue;
    const match: PersonaMatch = { name: p.name.trim(), imageUrl: p.imageUrl ?? null };
    const key = match.name.toLowerCase();
    if (key && !byCanonicalName.has(key)) byCanonicalName.set(key, match);
  }
  const resolve = buildCanonicalPersonaResolver((personas ?? []).map((p) => p.name));
  return (label: string) => {
    const canonical = resolve(label);
    return canonical ? byCanonicalName.get(canonical.toLowerCase()) ?? null : null;
  };
}

// ---------------------------------------------------------------------------
// Formatting + palette
// ---------------------------------------------------------------------------
// Producer colors — the ONLY way producers are distinguished, used
// consistently across scatter bubbles, funnel bars, trend lines, gallery
// badges, and legends. PersonaOS = the app's primary accent; in-house =
// neutral grey benchmark.
const VIOLET = "#6D3BEB";

const fmtUsd = (n: number | null, digits = 2) =>
  n === null || !isFinite(n) ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: n >= 100 ? 0 : digits })}`;
const fmtPct = (n: number | null, digits = 2) =>
  n === null || !isFinite(n) ? "—" : `${n.toFixed(digits)}%`;
const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : fmtInt(n);

const DATA_START = "2026-06-26"; // feed start date (guide §4)
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };

// Date range — Shopify-style selector (Task #2235): preset sidebar +
// "Last N unit / include today" input + two-month calendar, all editing a
// DRAFT that only commits on Apply. The applied choice is a selection
// (preset | lastN | custom) so the trigger can read "Last 7 days" instead of
// raw dates, and preset/lastN selections re-resolve relative to today on
// reload. Default = the "Last 7 days" preset.
type CpDateRange = { since: string; until: string }; // YYYY-MM-DD, inclusive
const isYmd = (s: unknown): s is string => {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [year, month, day] = s.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
};
const parseYmd = (s: string) => new Date(`${s}T00:00:00`);
// Calendar days arrive as LOCAL midnights — format them locally so a
// timezone ahead of UTC doesn't shift the picked day backwards.
const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d: Date, n: number) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };

export type CpUnit = "days" | "weeks" | "months";
type CpDateComparison = "none" | "previous" | "custom";
type CpRangeSelection =
  | { kind: "preset"; preset: CpPresetKey }
  | { kind: "lastN"; n: number; unit: CpUnit; includeToday: boolean }
  | { kind: "custom"; since: string; until: string };
const CP_UNITS: CpUnit[] = ["days", "weeks", "months"];

// YYYY-MM-DD strings compare lexicographically, so clamping to the feed
// window (DATA_START → today) happens in string space.
const clampRange = (since: string, until: string, today = ymd(new Date())): CpDateRange => {
  let s = since < DATA_START ? DATA_START : since;
  let u = until > today ? today : until;
  if (u < DATA_START) u = DATA_START;
  if (u > today) u = today;
  if (s > u) s = u;
  return { since: s, until: u };
};

// "Last N units": window ends at the anchor (today, or yesterday when
// "include today" is off). Inclusive window of exactly N days (or N×7 for weeks) ending at the anchor
// (N=7 + include today on Aug 11 → Aug 5–11).
function resolveLastN(n: number, unit: CpUnit, includeToday: boolean, now = new Date()): CpDateRange {
  const anchor = includeToday ? now : addDays(now, -1);
  let since: Date;
  if (unit === "days") since = addDays(anchor, -(n - 1));
  else if (unit === "weeks") since = addDays(anchor, -(7 * n - 1));
  else { since = new Date(anchor); since.setMonth(since.getMonth() - n); }
  return clampRange(ymd(since), ymd(anchor), ymd(now));
}

// Preset sidebar — adapted to the daily-granularity feed (no sub-day rows).
const CP_PRESETS = [
  { key: "last7", label: "Last 7 days", resolve: (now: Date) => resolveLastN(7, "days", true, now) },
  { key: "last30", label: "Last 30 days", resolve: (now: Date) => resolveLastN(30, "days", true, now) },
  { key: "last90", label: "Last 90 days", resolve: (now: Date) => resolveLastN(90, "days", true, now) },
  { key: "lastweek", label: "Last week", resolve: (now: Date) => {
    const end = addDays(now, -now.getDay() - 1); // last Saturday (weeks run Sun–Sat)
    return clampRange(ymd(addDays(end, -6)), ymd(end), ymd(now));
  } },
  { key: "lastmonth", label: "Last month", resolve: (now: Date) => {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return clampRange(ymdLocal(start), ymdLocal(end), ymd(now));
  } },
  { key: "lastquarter", label: "Last quarter", resolve: (now: Date) => {
    const q = Math.floor(now.getMonth() / 3); // current quarter index
    const start = new Date(now.getFullYear(), (q - 1) * 3, 1);
    const end = new Date(now.getFullYear(), q * 3, 0);
    return clampRange(ymdLocal(start), ymdLocal(end), ymd(now));
  } },
  { key: "lastyear", label: "Last year", resolve: (now: Date) => clampRange(`${now.getFullYear() - 1}-01-01`, `${now.getFullYear() - 1}-12-31`, ymd(now)) },
  { key: "alltime", label: "All time", resolve: (now: Date) => clampRange(DATA_START, ymd(now), ymd(now)) },
] as const;
type CpPresetKey = (typeof CP_PRESETS)[number]["key"];
const isPresetKey = (k: unknown): k is CpPresetKey => CP_PRESETS.some((p) => p.key === k);

const defaultSelection = (): CpRangeSelection => ({ kind: "preset", preset: "last7" });

export function resolveSelection(sel: CpRangeSelection, now = new Date()): CpDateRange {
  if (sel.kind === "preset") {
    const p = CP_PRESETS.find((x) => x.key === sel.preset) ?? CP_PRESETS[0];
    return p.resolve(now);
  }
  if (sel.kind === "lastN") return resolveLastN(sel.n, sel.unit, sel.includeToday, now);
  return clampRange(sel.since, sel.until, ymd(now));
}

// The previous period immediately precedes the selected period and has the
// same inclusive day count. Do not clamp here: a pre-feed range is unavailable,
// not a shortened comparison.
export function previousPeriodRange(range: CpDateRange): CpDateRange {
  const since = parseYmd(range.since);
  const until = parseYmd(range.until);
  const days = Math.round((until.getTime() - since.getTime()) / 86_400_000) + 1;
  return { since: ymdLocal(addDays(since, -days)), until: ymdLocal(addDays(since, -1)) };
}

// Trigger pill label: preset name / "Last N units" / explicit date span.
function selectionLabel(sel: CpRangeSelection, range: CpDateRange): string {
  if (sel.kind === "preset") return CP_PRESETS.find((p) => p.key === sel.preset)?.label ?? "Custom";
  if (sel.kind === "lastN") return `Last ${sel.n} ${sel.n === 1 ? sel.unit.slice(0, -1) : sel.unit}`;
  return `${fmtDateShort(range.since)} – ${fmtDateShort(range.until)}`;
}

// Footer summary, e.g. "Aug 4–11, 2026" / "Jun 26 – Aug 11, 2026".
function fmtRangeSummary(r: CpDateRange): string {
  const a = parseYmd(r.since);
  const b = parseYmd(r.until);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return `${r.since} – ${r.until}`;
  const mon = (d: Date) => d.toLocaleDateString(undefined, { month: "short" });
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return a.getDate() === b.getDate()
      ? `${mon(a)} ${a.getDate()}, ${a.getFullYear()}`
      : `${mon(a)} ${a.getDate()}–${b.getDate()}, ${a.getFullYear()}`;
  }
  if (a.getFullYear() === b.getFullYear()) return `${mon(a)} ${a.getDate()} – ${mon(b)} ${b.getDate()}, ${a.getFullYear()}`;
  return `${mon(a)} ${a.getDate()}, ${a.getFullYear()} – ${mon(b)} ${b.getDate()}, ${b.getFullYear()}`;
}

// Validate a persisted selection (prefs may hold older/corrupt shapes).
function parseStoredSelection(raw: unknown): CpRangeSelection | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  if (s.kind === "preset" && isPresetKey(s.preset)) return { kind: "preset", preset: s.preset };
  if (
    s.kind === "lastN" && typeof s.n === "number" && Number.isInteger(s.n) && s.n >= 1 && s.n <= 999 &&
    CP_UNITS.includes(s.unit as CpUnit) && typeof s.includeToday === "boolean"
  ) return { kind: "lastN", n: s.n, unit: s.unit as CpUnit, includeToday: s.includeToday };
  if (s.kind === "custom" && isYmd(s.since) && isYmd(s.until) && s.since <= s.until) return { kind: "custom", since: s.since, until: s.until };
  return null;
}

// Min-spend filter mode: "fade" keeps immature (below-gate) ads visible but
// translucent / "low data"; "hide" removes them from every section entirely.
type SpendFilterMode = "fade" | "hide";

// Per-brand persisted view preferences (localStorage) — applied date
// SELECTION (preset identity / lastN / explicit range), min-spend value, and
// filter mode survive a page reload. Backward compatible: older prefs that
// stored only `range` load as a custom selection; preset-string prefs from
// the pre-picker era fall back to the default preset.
type CpPrefs = {
  sel: CpRangeSelection;
  minSpend: number;
  spendFilterMode: SpendFilterMode;
  dateComparison: CpDateComparison;
  comparisonRange: CpDateRange | null;
};
const prefsKey = (brandId: string) => `creative-performance-prefs:${brandId}`;
function parseStoredComparisonRange(raw: unknown): CpDateRange | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const today = ymd(new Date());
  if (!isYmd(r.since) || !isYmd(r.until) || r.since > r.until || r.since < DATA_START || r.until > today) return null;
  return { since: r.since, until: r.until };
}
function loadCpPrefs(brandId: string | undefined): CpPrefs {
  const fallback: CpPrefs = {
    sel: defaultSelection(), minSpend: DEFAULT_MIN_SPEND, spendFilterMode: "fade", dateComparison: "none", comparisonRange: null,
  };
  if (!brandId) return fallback;
  try {
    const raw = localStorage.getItem(prefsKey(brandId));
    if (!raw) return fallback;
    const p = JSON.parse(raw);
    const stored = parseStoredSelection(p?.rangeSel);
    const comparisonRange = parseStoredComparisonRange(p?.comparisonRange);
    // Legacy shape: explicit { range } without a selection → custom range.
    const validRange = isYmd(p?.range?.since) && isYmd(p?.range?.until) && p.range.since <= p.range.until;
    return {
      sel: stored ?? (validRange ? { kind: "custom", since: p.range.since, until: p.range.until } : fallback.sel),
      minSpend: typeof p?.minSpend === "number" && p.minSpend >= 0 && p.minSpend <= 500 ? p.minSpend : fallback.minSpend,
      spendFilterMode: p?.spendFilterMode === "hide" ? "hide" : "fade",
      dateComparison: p?.dateComparison === "custom" && comparisonRange ? "custom" : p?.dateComparison === "previous" ? "previous" : "none",
      comparisonRange,
    };
  } catch {
    return fallback;
  }
}
function saveCpPrefs(brandId: string | undefined, prefs: CpPrefs) {
  if (!brandId) return;
  try {
    // `range` is written alongside the selection for backward/forward compat.
    localStorage.setItem(prefsKey(brandId), JSON.stringify({
      rangeSel: prefs.sel,
      range: resolveSelection(prefs.sel),
      minSpend: prefs.minSpend,
      spendFilterMode: prefs.spendFilterMode,
      dateComparison: prefs.dateComparison,
      comparisonRange: prefs.comparisonRange,
    }));
  } catch { /* storage unavailable — non-fatal */ }
}

// Date-range picker pill (Task #2235): Shopify-style popover — preset
// sidebar, "Last N unit + Include today" input, two-month calendar, live
// range summary, and explicit Cancel/Apply. Everything inside edits a DRAFT
// selection; the page's queries only rescope when Apply commits it.
// Cancel / Escape / clicking outside discards the draft.
function LegacyDateRangeControl({ sel, comparison, comparisonRange, comparisonEnabled, onApply }: {
  sel: CpRangeSelection;
  comparison: CpDateComparison;
  comparisonRange: CpDateRange | null;
  comparisonEnabled: boolean;
  onApply: (s: CpRangeSelection, comparison: CpDateComparison, comparisonRange: CpDateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<CpRangeSelection>(sel);
  const [draftComparison, setDraftComparison] = useState<CpDateComparison>(comparison);
  const [draftComparisonRange, setDraftComparisonRange] = useState<CpDateRange>(
    () => comparisonRange ?? previousPeriodRange(resolveSelection(sel)),
  );
  // Last-N input scratch state (N kept as a string so clearing the field
  // doesn't snap back to a number mid-edit).
  const [nText, setNText] = useState("7");
  const [unit, setUnit] = useState<CpUnit>("days");
  const [includeToday, setIncludeToday] = useState(true);
  // Start-only calendar click: half-picked range, held until the end click.
  const [calStart, setCalStart] = useState<Date | null>(null);

  const applied = resolveSelection(sel);
  const resolved = resolveSelection(draft);
  const label = selectionLabel(sel, applied);
  const customComparisonValid =
    isYmd(draftComparisonRange.since)
    && isYmd(draftComparisonRange.until)
    && draftComparisonRange.since >= DATA_START
    && draftComparisonRange.until <= ymd(new Date())
    && draftComparisonRange.since <= draftComparisonRange.until;

  const openWith = (o: boolean) => {
    setOpen(o);
    if (!o) return;
    // Re-seed the draft (and the Last-N scratch fields) from the applied
    // selection every time the popover opens.
    setDraft(sel);
    setDraftComparison(comparison);
    setDraftComparisonRange(comparisonRange ?? previousPeriodRange(resolveSelection(sel)));
    setCalStart(null);
    if (sel.kind === "lastN") {
      setNText(String(sel.n));
      setUnit(sel.unit);
      setIncludeToday(sel.includeToday);
    } else {
      setNText("7");
      setUnit("days");
      setIncludeToday(true);
    }
  };

  const setLastNDraft = (nextText: string, nextUnit: CpUnit, nextInclude: boolean) => {
    setNText(nextText);
    setUnit(nextUnit);
    setIncludeToday(nextInclude);
    setCalStart(null);
    const n = parseInt(nextText, 10);
    if (Number.isInteger(n) && n >= 1 && n <= 999) {
      setDraft({ kind: "lastN", n, unit: nextUnit, includeToday: nextInclude });
    }
  };

  const selected: DayPickerDateRange = calStart
    ? { from: calStart, to: undefined }
    : { from: parseYmd(resolved.since), to: parseYmd(resolved.until) };

  return (
    <Popover open={open} onOpenChange={openWith}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="button-date-range"
          className="h-7 px-3 rounded-full text-xs font-medium border bg-white dark:bg-card text-foreground/80 hover:border-violet-300 hover:text-foreground transition-colors inline-flex items-center gap-1.5"
        >
          <CalendarDays className="h-3.5 w-3.5 text-violet-600" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid="popover-date-range">
        <div className="flex">
          {/* Preset sidebar */}
          <div className="w-36 shrink-0 border-r py-2 px-1.5 space-y-0.5" data-testid="list-date-presets">
            {CP_PRESETS.map((p) => {
              const active = draft.kind === "preset" && draft.preset === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`button-preset-${p.key}`}
                  onClick={() => { setDraft({ kind: "preset", preset: p.key }); setCalStart(null); }}
                  className={`w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    active ? "bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300" : "text-foreground/80 hover:bg-accent hover:text-foreground"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col">
            {/* Last N unit + Include today */}
            <div className="flex items-center gap-2 border-b px-3 py-2.5">
              <span className="text-xs font-medium text-foreground/80">Last</span>
              <input
                type="number"
                min={1}
                max={999}
                value={nText}
                onChange={(e) => setLastNDraft(e.target.value, unit, includeToday)}
                data-testid="input-lastn"
                className="h-7 w-16 rounded-md border bg-white dark:bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <select
                value={unit}
                onChange={(e) => setLastNDraft(nText, e.target.value as CpUnit, includeToday)}
                data-testid="select-lastn-unit"
                className="h-7 rounded-md border bg-white dark:bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs font-medium text-foreground/80 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeToday}
                  onChange={(e) => setLastNDraft(nText, unit, e.target.checked)}
                  data-testid="checkbox-include-today"
                  className="h-3.5 w-3.5 accent-violet-600"
                />
                Include today
              </label>
            </div>
            {/* Two-month calendar — manual clicks turn the draft custom */}
            <Calendar
              mode="range"
              numberOfMonths={2}
              defaultMonth={selected.from}
              selected={selected}
              disabled={{ before: parseYmd(DATA_START), after: new Date() }}
              onSelect={(_r, day) => {
                if (!calStart) {
                  // First click starts a NEW range (single-day draft until
                  // the end date is chosen).
                  setCalStart(day);
                  setDraft({ kind: "custom", since: ymdLocal(day), until: ymdLocal(day) });
                  return;
                }
                const [a, b] = calStart.getTime() <= day.getTime() ? [calStart, day] : [day, calStart];
                setDraft({ kind: "custom", since: ymdLocal(a), until: ymdLocal(b) });
                setCalStart(null);
              }}
            />
            {comparisonEnabled && (
              <div className="border-t px-3 py-2.5 flex flex-wrap items-center gap-4 text-xs" data-testid="controls-date-comparison">
                <span className="font-medium text-foreground/80">Compare</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="date-comparison"
                    checked={draftComparison === "previous"}
                    onChange={() => setDraftComparison("previous")}
                    data-testid="radio-date-comparison-previous"
                  />
                  Previous period
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="date-comparison"
                    checked={draftComparison === "custom"}
                    onChange={() => setDraftComparison("custom")}
                    data-testid="radio-date-comparison-custom"
                  />
                  Custom range
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="date-comparison"
                    checked={draftComparison === "none"}
                    onChange={() => setDraftComparison("none")}
                    data-testid="radio-date-comparison-none"
                  />
                  None
                </label>
              </div>
            )}
            {comparisonEnabled && draftComparison === "custom" && (
              <div className="border-t px-3 py-2.5 space-y-1.5" data-testid="controls-date-comparison-custom">
                <div className="text-xs font-medium text-foreground/80">Comparison range</div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    min={DATA_START}
                    max={ymd(new Date())}
                    value={draftComparisonRange.since}
                    onChange={(e) => setDraftComparisonRange((r) => ({ ...r, since: e.target.value }))}
                    data-testid="input-comparison-since"
                    className="h-7 rounded-md border bg-white dark:bg-card px-2 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="date"
                    min={DATA_START}
                    max={ymd(new Date())}
                    value={draftComparisonRange.until}
                    onChange={(e) => setDraftComparisonRange((r) => ({ ...r, until: e.target.value }))}
                    data-testid="input-comparison-until"
                    className="h-7 rounded-md border bg-white dark:bg-card px-2 text-xs"
                  />
                </div>
                {!customComparisonValid && (
                  <p className="text-[11px] text-destructive" data-testid="text-comparison-range-invalid">
                    Choose an ordered range between {fmtDateShort(DATA_START)} and today.
                  </p>
                )}
              </div>
            )}
            {/* Footer: live summary + Cancel / Apply */}
            <div className="flex items-center justify-between gap-3 border-t px-3 py-2.5">
              <span className="text-xs font-medium text-foreground/80" data-testid="text-date-range-summary">
                {fmtRangeSummary(resolved)}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="button-date-cancel"
                  onClick={() => setOpen(false)}
                  className="h-7 px-3 rounded-md border text-xs font-medium text-foreground/80 hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="button-date-apply"
                  disabled={draftComparison === "custom" && !customComparisonValid}
                  onClick={() => { onApply(draft, draftComparison, draftComparisonRange); setOpen(false); }}
                  className="h-7 px-3 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Reference-style rounded-full pill button.
function Pill({ active, onClick, children, testId, className = "" }: {
  active?: boolean; onClick?: () => void; children: React.ReactNode; testId?: string; className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-7 px-3 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
        active
          ? "bg-violet-600 border-violet-600 text-white"
          : "bg-white dark:bg-card border-border text-foreground/80 hover:border-violet-300 hover:text-foreground"
      } ${className}`}
    >
      {children}
    </button>
  );
}

// Section card matching the reference: white, generous radius, soft border.
function SectionCard({ title, subtitle, children, testId, headerRight }: {
  title?: string; subtitle?: React.ReactNode; children: React.ReactNode;
  testId?: string; headerRight?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border bg-white dark:bg-card shadow-sm" data-testid={testId}>
      {(title || headerRight) && (
        <div className="px-6 pt-5 pb-1 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>}
            {subtitle && <p className="text-[13px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {headerRight}
        </div>
      )}
      <div className="px-6 pb-6 pt-3">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function CreativePerformancePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, setLocation] = useLocation();

  const { data: brand } = useQuery<Brand>({ queryKey: ["/api/brands", brandId], enabled: !!brandId });
  const brandName = brand?.name;
  const cfg = parseCreativePerformanceSettings(brand?.settings);
  const scopeError = useCreativeFeedScopeError(brandId);

  // Global controls — preset, min-spend, and filter mode hydrate from the
  // per-brand localStorage prefs and persist on every change.
  const [dateSel, setDateSel] = useState<CpRangeSelection>(() => loadCpPrefs(brandId).sel);
  const [dateComparison, setDateComparison] = useState<CpDateComparison>(() => loadCpPrefs(brandId).dateComparison);
  const [customComparisonRange, setCustomComparisonRange] = useState<CpDateRange | null>(
    () => loadCpPrefs(brandId).comparisonRange,
  );
  // Preset / lastN selections re-resolve relative to today; explicit ranges
  // pass through (clamped to the feed window).
  const range = useMemo(() => resolveSelection(dateSel), [dateSel]);
  const [tier, setTier] = useState<CreativeTier | null>(null); // null → brand default
  const [excludeBoosted, setExcludeBoosted] = useState(true);
  const [minSpend, setMinSpend] = useState(() => loadCpPrefs(brandId).minSpend);
  const [spendFilterMode, setSpendFilterMode] = useState<SpendFilterMode>(() => loadCpPrefs(brandId).spendFilterMode);
  const [detailAd, setDetailAd] = useState<CreativeAdRow | null>(null);
  // Persona drill-down. Deep-linkable via ?persona=<feed label> — read once
  // on mount, then kept in sync with replaceState so back-to-dashboard never
  // stacks history entries.
  const [selectedPersona, setSelectedPersona] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("persona"),
  );
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedPersona) url.searchParams.set("persona", selectedPersona);
    else url.searchParams.delete("persona");
    window.history.replaceState(null, "", url.toString());
  }, [selectedPersona]);
  const [gallerySort, setGallerySort] = useState<"spend" | "costPerAtc" | "leads" | "cpl">("spend");
  const [producerMode, setProducerMode] = useState<"both" | ProducerGroup>("both");
  // Legacy persona drill-down (Task #2254): one ad-feed persona selection
  // drives the legacy funnel, trend chart, and quadrant. Account-funnel groups
  // use their own stable-key state below because their dynamic grouping does
  // not necessarily map to the ad feed's persona labels.
  const [drillPersona, setDrillPersona] = useState<string | null>(null);
  const [funnelGroupKey, setFunnelGroupKey] = useState<string | null>(null);
  const effectiveTier: CreativeTier = tier ?? cfg.defaultTier ?? "Cold";
  const since = range.since;
  const until = range.until;
  const minImpr = impressionsGateFor(minSpend);
  const priorRange = useMemo(() => previousPeriodRange(range), [range]);
  const resolvedComparisonRange =
    dateComparison === "custom" && customComparisonRange ? customComparisonRange : priorRange;
  const comparisonRequested =
    cfg.dateComparisonEnabled && cfg.leadGenEnabled && dateComparison !== "none";
  const comparisonPredatesFeed = resolvedComparisonRange.since < DATA_START;

  const qs = `since=${since}&until=${until}&tier=${effectiveTier}`;
  const adsQuery = useQuery<CreativeAdRow[]>({
    ...creativeFeedQueryOptions<CreativeAdRow[]>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/ads?${qs}`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  const priorQs = `since=${resolvedComparisonRange.since}&until=${resolvedComparisonRange.until}&tier=${effectiveTier}`;
  const priorAdsQuery = useQuery<CreativeAdRow[]>({
    ...creativeFeedQueryOptions<CreativeAdRow[]>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/ads?${priorQs}`],
    enabled: !!brandId && comparisonRequested && !comparisonPredatesFeed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const trendQuery = useQuery<CreativeTrendPoint[]>({
    ...creativeFeedQueryOptions<CreativeTrendPoint[]>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/trend?${qs}`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  // Kaya's custom-conversions report accepts only the dashboard date range.
  // The server injects client=kaya and the shared analytics credential, so the
  // browser cannot choose an account/client or leak the API key. This is
  // opt-in and leaves every other brand on its existing dashboard behavior.
  const funnelEnabled =
    !!brandId
    && brand?.name.trim().toLowerCase() === "kaya"
    && cfg.accountId === KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID
    && cfg.primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP;
  // The custom Kaya reservation scorecard/funnel stays authoritative, while
  // every ad-level section uses the canonical per-ad `leads` field.
  const adLeadMetricsEnabled = cfg.leadGenEnabled || funnelEnabled;
  const leadSourceField: LeadSourceField = funnelEnabled || cfg.leadEventName ? "leads" : cfg.leadSourceField;
  useEffect(() => {
    setGallerySort(adLeadMetricsEnabled ? "leads" : "spend");
  }, [adLeadMetricsEnabled]);
  const funnelQs = `since=${since}&until=${until}`;
  const funnelQuery = useQuery<CreativeFunnelResponse>({
    ...creativeFeedQueryOptions<CreativeFunnelResponse>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/funnel?${funnelQs}`],
    enabled: funnelEnabled,
    staleTime: 5 * 60 * 1000,
  });
  // Brand personas, matched by name so the persona sections can show the
  // actual PersonaOS avatar next to each feed persona label.
  const personasQuery = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  const personaFor = useMemo(
    () => buildPersonaLookup(personasQuery.data),
    [personasQuery.data],
  );
  const resolvePersona = useMemo(
    () => buildCanonicalPersonaResolver((personasQuery.data ?? []).map((p) => p.name)),
    [personasQuery.data],
  );
  // Preserve old persona deep links when a feed abbreviation now resolves to a
  // stored canonical identity, so the detail view filters the same rows as the
  // rest of the dashboard.
  useEffect(() => {
    const canonical = selectedPersona ? resolvePersona(selectedPersona) : null;
    if (canonical && canonical !== selectedPersona) setSelectedPersona(canonical);
  }, [selectedPersona, resolvePersona]);
  // Local library ad-name → angle/generation linkage for the drill-down.
  // Fail-open: if the endpoint errors, everything groups under Unknown angle.
  const adLinksQuery = useQuery<CreativeAdLink[]>({
    queryKey: [`/api/brands/${brandId}/creative-performance/ad-links`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  // Brand narrative angles: (a) their names feed the parser-based fallback
  // attribution (ads named by the PersonaOS convention but missing a library
  // row), and (b) their details render in the drill-down's angle breakdown.
  // Fail-open like ad-links.
  const anglesQuery = useQuery<CreativeAngleDetail[]>({
    queryKey: [`/api/brands/${brandId}/creative-performance/angles`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  // Brand products (Task #2313): stored product names validate the product
  // segment extracted from agency-convention ad names. Fail-open like the
  // other lookups.
  const productsQuery = useQuery<Array<{ name: string | null }>>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
  });
  const enrichFor = useMemo(
    () => buildCreativeAttributionLookup({
      links: adLinksQuery.data,
      knownAngleNames: (anglesQuery.data ?? []).map((a) => a.name),
      personaNames: (personasQuery.data ?? []).map((p) => p.name),
      knownProductNames: (productsQuery.data ?? []).map((p) => p.name),
    }),
    [adLinksQuery.data, anglesQuery.data, personasQuery.data, productsQuery.data],
  );
  const angleDetailFor = useMemo(
    () => buildAngleDetailLookup(anglesQuery.data),
    [anglesQuery.data],
  );

  // Product fallback (Task #2313): agency-run feeds label every row with a
  // generic product ("Other/Mixed") — derive the real product from the agency
  // ad name where it validates against a stored product name.
  const productForAdName = useMemo(
    () => buildProductNameLookup((productsQuery.data ?? []).map((p) => p.name)),
    [productsQuery.data],
  );
  const allAds = useMemo(
    () => fillMissingProducts(
      canonicalizeCreativePerformancePersonas(
        applyCreativeAttribution(adsQuery.data ?? [], enrichFor),
        resolvePersona,
      ),
      productForAdName,
    ),
    [adsQuery.data, enrichFor, resolvePersona, productForAdName],
  );
  const currentDetailAd = detailAd
    ? allAds.find((candidate) => candidate.adId === detailAd.adId) ?? detailAd
    : null;
  const sourceLink = useCreativePerformanceAdLink({
    brandId, ads: allAds, ready: adsQuery.isSuccess, feedStart: DATA_START, onSelect: setDetailAd,
  });
  const priorAllAds = useMemo(
    () => fillMissingProducts(
      canonicalizeCreativePerformancePersonas(
        applyCreativeAttribution(priorAdsQuery.data ?? [], enrichFor),
        resolvePersona,
      ),
      productForAdName,
    ),
    [priorAdsQuery.data, enrichFor, resolvePersona, productForAdName],
  );
  // The in-house group is labeled with the brand's own producer name from the
  // feed (e.g. "Naadam"), never the generic "In-house", per user preference.
  const inhouseLabel = useMemo(
    () => deriveInhouseLabel(allAds, brand?.name),
    [allAds, brand?.name],
  );
  // Boosted filter applies to everything; maturity filter applies to
  // rankings/aggregates only (immature ads still render translucent).
  const filteredAds = useMemo(
    () => allAds.filter((a) => !excludeBoosted || !a.isBoosted),
    [allAds, excludeBoosted],
  );
  // Producer selection only exists for comparison-enabled brands; everyone
  // else is always in "both" (the pills are hidden and the state never moves).
  const effectiveProducerMode = cfg.comparisonEnabled ? producerMode : "both";
  const ads = useMemo(
    () => (effectiveProducerMode === "both" ? filteredAds : filteredAds.filter((a) => classifyProducer(a) === effectiveProducerMode)),
    [filteredAds, effectiveProducerMode],
  );
  const matureAds = useMemo(
    () => ads.filter((a) => isMatureAd(a, minSpend, minImpr)),
    [ads, minSpend, minImpr],
  );
  const agg = useMemo(() => aggregateAds(matureAds), [matureAds]);
  // Apply the exact same boosted, producer, and maturity rules to the prior
  // feed. Account, tier, and lead-event rules are already shared by using the
  // same brand-scoped endpoint and effective tier.
  const priorMatureAds = useMemo(() => {
    const boostedScoped = priorAllAds.filter((a) => !excludeBoosted || !a.isBoosted);
    const producerScoped = effectiveProducerMode === "both"
      ? boostedScoped
      : boostedScoped.filter((a) => classifyProducer(a) === effectiveProducerMode);
    return producerScoped.filter((a) => isMatureAd(a, minSpend, minImpr));
  }, [priorAllAds, excludeBoosted, effectiveProducerMode, minSpend, minImpr]);
  const priorAgg = useMemo(() => aggregateAds(priorMatureAds), [priorMatureAds]);
  // Feed freshness — the upstream mirror can lag a day or two behind Meta.
  // When the newest lastDate across the (unfiltered) rows is older than both
  // yesterday and the requested window end, surface a quiet note so numbers
  // aren't mistaken for up-to-the-minute. YYYY-MM-DD strings compare lexically.
  const staleDataDate = useMemo(() => {
    const rows = adsQuery.data ?? [];
    if (rows.length === 0) return null;
    let latest = "";
    for (const r of rows) if (r.lastDate && r.lastDate > latest) latest = r.lastDate;
    if (!latest) return null;
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return latest < yesterday && latest < until ? latest : null;
  }, [adsQuery.data, until]);
  // Missing-day coverage (Task #2465): days inside [since, until] that have
  // no trend data at all. Uses raw trendQuery.data (all producers, unfiltered)
  // so the detected set of covered dates is not affected by the producer-mode
  // or boosted filters. Trailing days are only exempted when the range is
  // recent (ends today or yesterday) — historical windows report all gaps.
  const missingDaySpans = useMemo(
    () => detectMissingDaySpans(trendQuery.data ?? [], since, until),
    [trendQuery.data, since, until],
  );
  // Trend-specific freshness note: when the trend feed lags behind the
  // selected window's end (and the range is current), surface the trend's
  // own data-through date — the existing staleDataDate uses the ads feed,
  // which can be fresher than the trend mirror.
  const trendStaleDate = useMemo(() => {
    const points = trendQuery.data ?? [];
    if (points.length === 0) return null;
    let latest = "";
    for (const p of points) if (p.date && p.date > latest) latest = p.date;
    if (!latest) return null;
    const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
    return latest < yesterday && latest < until ? latest : null;
  }, [trendQuery.data, until]);

  // Hide mode removes below-gate ads from EVERY section (health strip,
  // quadrant, gallery) — fade mode keeps them visible but de-emphasized.
  const hiding = spendFilterMode === "hide";
  const sectionAds = hiding ? matureAds : ads;
  const hiddenCount = hiding ? ads.length - matureAds.length : 0;
  const hiddenNote = hiding
    ? `Ads under $${minSpend} / ${fmtCompact(minImpr)} impressions hidden${hiddenCount > 0 ? ` (${hiddenCount})` : ""}.`
    : null;

  // Persist view prefs per brand so the chosen window/filter survive reloads.
  // Navigating brand A → brand B reuses this mounted component, so on a
  // brandId change we rehydrate all three states from the new brand's key and
  // suppress the persistence write for that commit — otherwise brand A's
  // stale state would overwrite brand B's saved preferences.
  const hydratedBrandRef = useRef(brandId);
  const skipPersistRef = useRef(false);
  useEffect(() => {
    if (hydratedBrandRef.current === brandId) return;
    hydratedBrandRef.current = brandId;
    const p = loadCpPrefs(brandId);
    // Only suppress the next persist when hydration actually changes state —
    // if nothing changes there is no follow-up render, and an unconditional
    // skip would swallow the user's next real change instead.
    skipPersistRef.current = JSON.stringify(p.sel) !== JSON.stringify(dateSel)
      || p.minSpend !== minSpend
      || p.spendFilterMode !== spendFilterMode
      || p.dateComparison !== dateComparison
      || JSON.stringify(p.comparisonRange) !== JSON.stringify(customComparisonRange);
    setDateSel(p.sel);
    setMinSpend(p.minSpend);
    setSpendFilterMode(p.spendFilterMode);
    setDateComparison(p.dateComparison);
    setCustomComparisonRange(p.comparisonRange);
  }, [brandId]);
  useEffect(() => {
    if (skipPersistRef.current) { skipPersistRef.current = false; return; }
    saveCpPrefs(brandId, {
      sel: dateSel,
      minSpend,
      spendFilterMode,
      dateComparison,
      comparisonRange: customComparisonRange,
    });
  }, [brandId, dateSel, minSpend, spendFilterMode, dateComparison, customComparisonRange]);

  // Per-producer aggregates for Both mode — the maturity gate applies within
  // each group BEFORE comparing, and every ratio comes from summed counts.
  const producerAggs = useMemo(() => {
    const groups = splitAdsByProducer(filteredAds);
    return {
      personaos: aggregateAds(groups.personaos.filter((a) => isMatureAd(a, minSpend, minImpr))),
      inhouse: aggregateAds(groups.inhouse.filter((a) => isMatureAd(a, minSpend, minImpr))),
    };
  }, [filteredAds, minSpend, minImpr]);

  // Fairness guardrail: producer comparison is only meaningful within one
  // tier. Comparison is confined to the TOP-LEVEL delta scorecard, gated on
  // the brand's opt-in flag + Both mode + a single tier.
  const comparisonFair = effectiveTier !== "All";
  const comparing = cfg.comparisonEnabled && effectiveProducerMode === "both" && comparisonFair;

  // Boosted exclusion applies to the trend feed too (mergeTrendByDate drops
  // boosted rows) — every section on this page, including the persona trend
  // endpoint, honors the same excludeBoosted default (ON).
  const trend = useMemo(() => {
    const raw = trendQuery.data ?? [];
    if (effectiveProducerMode === "both") return mergeTrendByDate(raw, excludeBoosted);
    return mergeTrendByDate(raw.filter((p) => classifyProducer(p) === effectiveProducerMode), excludeBoosted);
  }, [trendQuery.data, excludeBoosted, effectiveProducerMode]);

  // Persona options for the shared drill-down pills: personas with at least
  // one mature ad, biggest spend first. Top 5 also plot in the quadrant.
  const personaOptions = useMemo(() => {
    const bySpend = new Map<string, number>();
    for (const a of matureAds) {
      if (!a.persona) continue;
      bySpend.set(a.persona, (bySpend.get(a.persona) || 0) + a.spend);
    }
    return Array.from(bySpend.entries()).sort((a, b) => b[1] - a[1]).map(([name]) => name);
  }, [matureAds]);
  const funnelGroupKeys = useMemo(
    () => funnelQuery.data?.groups.map((group) => group.key) ?? [],
    [funnelQuery.data],
  );
  // Shared persona → color mapping (mature-spend order) so the trend lines and
  // the quadrant dots/legend always agree on a persona's color.
  const personaColors = useMemo(
    () => new Map(personaOptions.slice(0, QUADRANT_COLORS.length).map((p, i) => [p, QUADRANT_COLORS[i]] as const)),
    [personaOptions],
  );
  // Clear a drilled persona that fell out of the selection (filters changed).
  useEffect(() => {
    if (drillPersona && !personaOptions.includes(drillPersona)) setDrillPersona(null);
  }, [drillPersona, personaOptions]);
  // Funnel group identity is response-defined and key-based. Clear it when a
  // filter change returns a different group set or funnel mode is disabled.
  useEffect(() => {
    if (funnelGroupKey && !funnelGroupKeys.includes(funnelGroupKey)) setFunnelGroupKey(null);
  }, [funnelGroupKey, funnelGroupKeys]);

  const reservationLeadError = funnelEnabled
    ? [adsQuery.error, trendQuery.error].find((error) =>
        ((error as Error | null)?.message || "").includes("leadEventUnavailable"),
      )
    : null;
  if (reservationLeadError) {
    return (
      <div className="p-6 max-w-3xl">
        <h1 className="text-xl font-semibold mb-3" data-testid="text-cp-title">Creative Performance</h1>
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2" data-testid="text-kaya-reservation-lead-error">
          <div className="font-medium text-destructive flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Completed reservation data is unavailable
          </div>
          <div className="text-xs text-muted-foreground">
            Kaya's completed OpenTable reservation event (ot_reservation) could not be loaded. No add-to-cart or other ecommerce metric has been substituted.
          </div>
          <Button size="sm" variant="outline" onClick={() => { adsQuery.refetch(); trendQuery.refetch(); }}>Retry</Button>
        </div>
      </div>
    );
  }

  if (scopeError || (!adsQuery.isLoading && adsQuery.error && (!adsQuery.data || (adsQuery.error as Error & { fatal?: boolean }).fatal))) {
    const errMsg = (scopeError || adsQuery.error as Error).message || "";
    // Landing here on a brand without the feature (e.g. after switching brands
    // while on this page) is expected — show a friendly state, not an error.
    if (errMsg.includes("notConfigured")) {
      return (
        <div className="p-6 md:p-8 max-w-3xl">
          <h1 className="text-xl font-semibold mb-3" data-testid="text-cp-title">Creative Performance</h1>
          <div className="rounded-2xl border bg-neutral-50/80 dark:bg-muted/30 p-6 text-sm space-y-3" data-testid="text-cp-not-enabled">
            <div className="flex items-center gap-2 font-medium"><Gauge className="h-5 w-5 text-violet-600" /> Not enabled for this brand</div>
            <p className="text-muted-foreground">
              Creative Performance isn't set up for {brandName || "this brand"} yet. It can be enabled per brand from the admin Analytics settings.
            </p>
            <Button size="sm" variant="outline" onClick={() => setLocation(`/brands/${brandId}`)} data-testid="button-cp-back-overview">
              Back to brand overview
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="p-6 max-w-3xl">
        <h1 className="text-xl font-semibold mb-3" data-testid="text-cp-title">Creative Performance</h1>
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2" data-testid="text-cp-error">
          <div className="font-medium text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Could not load creative performance data</div>
          <div className="text-xs text-muted-foreground break-all">{errMsg}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => adsQuery.refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-neutral-50 dark:bg-background">
      <div className="p-6 md:p-8 space-y-5 max-w-6xl mx-auto" data-testid="page-creative-performance">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5" data-testid="text-cp-title">
              <Gauge className="h-6 w-6 text-violet-600" /> Creative Performance
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {comparing
              ? <>PersonaOS vs. {inhouseLabel} creative — {brand?.name}, {since} → {until}.</>
              : <>Ad account creative — {brand?.name}, {since} → {until}.</>}{" "}
            CTR shown is all-click CTR (no video-view data in this feed).
          </p>
          <CreativePerformanceRefresh brandId={brandId!} snapshots={[
            (adsQuery.data as CreativeSavedData<CreativeAdRow[]> | undefined)?.snapshot,
            (trendQuery.data as CreativeSavedData<CreativeTrendPoint[]> | undefined)?.snapshot,
            comparisonRequested ? (priorAdsQuery.data as CreativeSavedData<CreativeAdRow[]> | undefined)?.snapshot : undefined,
            funnelEnabled ? (funnelQuery.data as CreativeSavedData<CreativeFunnelResponse> | undefined)?.snapshot : undefined,
          ]} requestError={!!adsQuery.error || !!trendQuery.error} />
        </div>

        {sourceLink.status && (
          <div role="status" className="rounded-xl border bg-white dark:bg-card p-3 text-sm flex flex-wrap items-center gap-3" data-testid="performance-source-status">
            <span>
              {sourceLink.status === "loading" ? "Finding the original source ad…"
                : sourceLink.status === "error" ? "Could not load the source ad. Retry to check Creative Performance again."
                : sourceLink.status === "missing" ? "This source ad is no longer available in Creative Performance. It may have been removed from the connected account or its available history."
                : "Source ad shown using all available dates and tiers. Your dashboard filters are unchanged."}
            </span>
            {sourceLink.status === "error" && <Button variant="outline" size="sm" onClick={sourceLink.retry}>Retry source lookup</Button>}
            {sourceLink.status !== "loading" && <Button variant="ghost" size="sm" onClick={sourceLink.clear}>Dismiss</Button>}
          </div>
        )}

        {/* 1 — Global controls */}
        <div className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <DateRangeControl
              sel={dateSel}
              comparison={dateComparison}
              comparisonRange={customComparisonRange}
              comparisonEnabled={cfg.dateComparisonEnabled && cfg.leadGenEnabled}
              onApply={(nextSel, nextComparison, nextComparisonRange) => {
                setDateSel(nextSel);
                setDateComparison(nextComparison);
                setCustomComparisonRange(nextComparisonRange);
              }}
            />
            <div className="flex items-center rounded-full border bg-white dark:bg-card p-0.5" data-testid="controls-tier-pills">
              {CREATIVE_TIERS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  data-testid={`button-tier-${t.toLowerCase()}`}
                  className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${effectiveTier === t ? "bg-violet-600 text-white" : "text-foreground/70 hover:text-foreground"}`}
                >
                  {t}
                </button>
              ))}
            </div>
            {cfg.comparisonEnabled && (
            <div className="flex items-center rounded-full border bg-white dark:bg-card p-0.5" data-testid="controls-producer-pills">
              {([
                ["both", "Both"],
                ["personaos", PRODUCER_LABELS.personaos],
                ["inhouse", inhouseLabel],
              ] as Array<["both" | ProducerGroup, string]>).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setProducerMode(mode)}
                  data-testid={`button-producer-${mode}`}
                  className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${producerMode === mode ? "bg-violet-600 text-white" : "text-foreground/70 hover:text-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            )}
            <button
              type="button"
              onClick={() => setExcludeBoosted(!excludeBoosted)}
              data-testid="switch-exclude-boosted"
              className={`h-7 px-3 rounded-full text-xs font-medium border transition-colors ${
                excludeBoosted
                  ? "bg-violet-100 dark:bg-violet-950/50 border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300"
                  : "bg-white dark:bg-card border-border text-foreground/70"
              }`}
            >
              Exclude boosted {excludeBoosted ? "✓" : ""}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2.5 rounded-full border bg-white dark:bg-card h-8 pl-3.5 pr-1">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Min spend</span>
              <Slider value={[minSpend]} min={0} max={500} step={25} onValueChange={(v) => setMinSpend(v[0])} className="w-28 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-violet-600 [&_.bg-primary]:bg-violet-600" data-testid="slider-min-spend" />
              <span className="text-xs font-semibold bg-violet-600 text-white rounded-full px-2.5 py-1 whitespace-nowrap">
                ${minSpend} / {fmtCompact(minImpr)} impr
              </span>
            </div>
            {/* Min-spend toggle contract (Task #2254 audit): the maturity gate
                (min spend + 20× impressions) ALWAYS excludes low-data ads from
                aggregates, rankings, and verdicts, in both modes. The toggle
                only controls display of below-gate ads: "fade" keeps them
                visible but translucent; "hide" removes them from every section
                (quadrant, personas, gallery) via sectionAds. */}
            <div className="flex items-center rounded-full border bg-white dark:bg-card p-0.5" data-testid="controls-spend-filter-mode">
              {([
                ["fade", "Fade low-spend", "Below-gate ads stay visible (translucent) but never count toward metrics or verdicts"],
                ["hide", "Hide low-spend", "Below-gate ads are removed from every section; metrics are unchanged (they were already excluded)"],
              ] as Array<[SpendFilterMode, string, string]>).map(([mode, label, tip]) => (
                <button
                  key={mode}
                  type="button"
                  title={tip}
                  onClick={() => setSpendFilterMode(mode)}
                  data-testid={`button-spend-filter-${mode}`}
                  className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${spendFilterMode === mode ? "bg-violet-600 text-white" : "text-foreground/70 hover:text-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="text-xs text-muted-foreground" data-testid="badge-attribution">Attribution: 7-day click / 1-day view</span>
            {staleDataDate && (
              <span className="text-xs text-muted-foreground" data-testid="badge-data-freshness" title="The analytics feed refreshes on a delay; newer activity isn't reflected yet.">
                Data through {fmtDateShort(staleDataDate)}
              </span>
            )}
            {/* When the trend mirror lags further than the ads feed (or when
                the ads feed is current but trend data is still behind), surface
                the trend's own data-through date so users know the trend chart
                and health-strip spend numbers stop earlier than the ad table. */}
            {trendStaleDate && trendStaleDate !== staleDataDate && (
              <span className="text-xs text-muted-foreground" data-testid="badge-trend-freshness" title="The trend feed may lag behind the ad-level feed; trend chart data ends here.">
                Trend data through {fmtDateShort(trendStaleDate)}
              </span>
            )}
            {effectiveTier === "All" && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> "All" mixes tiers with very different economics — aggregate metrics are indicative only.
              </span>
            )}
          </div>
        </div>

        {/* Missing-day coverage warning (Task #2465): fires only when some
            days inside the selected range have no trend data at all — not for
            trailing lag (already covered by the "Data through X" freshness
            note) and not when the feed simply has no rows yet (empty state). */}
        {missingDaySpans.length > 0 && (
          <div
            className="rounded-2xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 px-5 py-3 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2.5"
            data-testid="banner-missing-days"
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
            <span>
              <strong>Incomplete data for this range.</strong>{" "}
              No data found for{" "}
              <span data-testid="text-missing-day-spans">
                {missingDaySpans.map(formatMissingDaySpan).join(", ")}
              </span>
              . Metrics shown may be understated.
            </span>
          </div>
        )}

        {adsQuery.isLoading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 w-full rounded-2xl" />)}</div>
        ) : selectedPersona !== null ? (
          <PersonaDetailView
            personaLabel={selectedPersona}
            ads={ads.filter((a) => a.persona === selectedPersona)}
            minSpend={minSpend}
            minImpr={minImpr}
            enrichFor={enrichFor}
            angleDetailFor={angleDetailFor}
            personaFor={personaFor}
            onBack={() => setSelectedPersona(null)}
            onSelect={setDetailAd}
            leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined}
          />
        ) : (
          <>
            {/* Lead dashboards consolidate their selection count into the lead
                scorecard. E-commerce retains its standalone health strip and
                30-second verdict. */}
            {!adLeadMetricsEnabled && (
              <HealthStrip ads={sectionAds} matureCount={matureAds.length} trend={trend} />
            )}
            {funnelEnabled ? (
              <AccountFunnelScorecard
                step={selectedCreativeFunnelStep(funnelQuery.data, cfg.primaryConversionStep)}
                loading={funnelQuery.isLoading}
                errored={!!funnelQuery.error && !funnelQuery.data}
                spend={funnelQuery.data?.overall.spend ?? null}
                minSpend={minSpend}
                minImpr={minImpr}
                excludeBoosted={excludeBoosted}
                selectionAdCount={sectionAds.length}
              />
            ) : cfg.leadGenEnabled && comparisonRequested ? (
              <LeadGenDateComparisonScorecard
                agg={agg}
                priorAgg={priorAgg}
                leadSourceField={cfg.leadEventName ? "leads" : cfg.leadSourceField}
                minSpend={minSpend}
                minImpr={minImpr}
                excludeBoosted={excludeBoosted}
                selectionAdCount={sectionAds.length}
                currentRange={range}
                comparisonKind={dateComparison}
                priorRange={resolvedComparisonRange}
                priorLoading={priorAdsQuery.isLoading}
                priorUnavailable={comparisonPredatesFeed || (!!priorAdsQuery.error && !priorAdsQuery.data)}
              />
            ) : comparing ? (
              cfg.leadGenEnabled ? (
                <LeadGenComparisonScorecard
                  aggs={producerAggs}
                  leadSourceField={cfg.leadEventName ? "leads" : cfg.leadSourceField}
                  minSpend={minSpend}
                  minImpr={minImpr}
                  excludeBoosted={excludeBoosted}
                  tier={effectiveTier}
                  inhouseLabel={inhouseLabel}
                  selectionAdCount={sectionAds.length}
                />
              ) : (
                <ComparisonScorecard aggs={producerAggs} minSpend={minSpend} minImpr={minImpr} excludeBoosted={excludeBoosted} tier={effectiveTier} inhouseLabel={inhouseLabel} />
              )
            ) : (
              <>
                {cfg.comparisonEnabled && effectiveProducerMode === "both" && !comparisonFair && (
                  <div className="rounded-2xl border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-6 py-3 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2" data-testid="text-comparison-tier-caveat">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Producer comparison is hidden while "All" tiers are selected — mixing tiers with different economics makes the PersonaOS vs {inhouseLabel} comparison unfair. Pick a single tier (e.g. Cold) to compare.
                  </div>
                )}
                {cfg.leadGenEnabled ? (
                  <LeadGenScorecard agg={agg} leadSourceField={cfg.leadEventName ? "leads" : cfg.leadSourceField} minSpend={minSpend} minImpr={minImpr} excludeBoosted={excludeBoosted} selectionAdCount={sectionAds.length} />
                ) : (
                  <Scorecard agg={agg} minSpend={minSpend} minImpr={minImpr} excludeBoosted={excludeBoosted} />
                )}
              </>
            )}
            {/* Min-spend "hide" mode removes below-gate ads from the persona
                sections too (sectionAds) — in fade mode they stay visible
                and the verdict helpers gate internally. */}
            {/* User-requested order: the efficiency funnel sits DIRECTLY under
                the scorecard block, with "Are we improving?" under it, then
                the persona breakdown and quadrant. */}
            {funnelEnabled ? (
              <AccountFunnelSection
                data={funnelQuery.data}
                loading={funnelQuery.isLoading}
                errored={!!funnelQuery.error && !funnelQuery.data}
                primaryStep={KAYA_PRIMARY_CONVERSION_STEP}
                minSpend={minSpend}
                excludeBoosted={excludeBoosted}
                selectedGroupKey={funnelGroupKey}
                setSelectedGroupKey={setFunnelGroupKey}
              />
            ) : cfg.leadGenEnabled ? (
              <LeadGenFunnelSection
                ads={sectionAds}
                minSpend={minSpend}
                minImpr={minImpr}
                leadSourceField={cfg.leadEventName ? "leads" : cfg.leadSourceField}
                drillPersona={drillPersona}
                setDrillPersona={setDrillPersona}
                personaOptions={personaOptions}
                comparing={comparing}
                inhouseLabel={inhouseLabel}
              />
            ) : (
              <FunnelSection ads={sectionAds} minSpend={minSpend} minImpr={minImpr} drillPersona={drillPersona} setDrillPersona={setDrillPersona} personaOptions={personaOptions} comparing={comparing} inhouseLabel={inhouseLabel} />
            )}
            <TrendSection trend={trend} loading={trendQuery.isLoading} brandId={brandId} since={since} until={until} tier={effectiveTier} excludeBoosted={excludeBoosted} producerMode={effectiveProducerMode} minSpend={minSpend} minImpr={minImpr} drillPersona={drillPersona} setDrillPersona={setDrillPersona} personaOptions={personaOptions} personaColors={personaColors} leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined} />
            <PersonaSections ads={sectionAds} minSpend={minSpend} minImpr={minImpr} enrichFor={enrichFor} onOpenPersona={setSelectedPersona} personaFor={personaFor} leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined} />
            <QuadrantSection ads={sectionAds} minSpend={minSpend} minImpr={minImpr} personaColors={personaColors} onSelect={setDetailAd} drillPersona={drillPersona} hiddenNote={hiddenNote} leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined} />
            <GallerySection ads={sectionAds} minSpend={minSpend} minImpr={minImpr} sort={gallerySort} setSort={setGallerySort} onSelect={setDetailAd} inhouseLabel={inhouseLabel} hiddenNote={hiddenNote} leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined} />
          </>
        )}

        <AdDetailDialog brandId={brandId} ad={currentDetailAd} onClose={() => { setDetailAd(null); sourceLink.clear(); }} inhouseLabel={inhouseLabel} enrichFor={enrichFor} leadSourceField={adLeadMetricsEnabled ? leadSourceField : undefined} sourceHistory={sourceLink.status === "historical"} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2 — Health strip
// ---------------------------------------------------------------------------
function HealthStrip({ ads, matureCount, trend }: {
  ads: CreativeAdRow[]; matureCount: number;
  trend: Array<{ date: string; spend: number }>;
}) {
  const totalSpend = ads.reduce((s, a) => s + a.spend, 0);
  // Zero-spend days between first and last active (spend > 0) trend day.
  const active = trend.filter((t) => t.spend > 0);
  let zeroDates: string[] = [];
  if (active.length >= 2) {
    const first = active[0].date, last = active[active.length - 1].date;
    zeroDates = trend.filter((t) => t.date > first && t.date < last && t.spend === 0).map((t) => t.date);
  }
  const items: Array<{ label: string; value: string; warn?: boolean }> = [
    { label: "Ads in selection", value: fmtInt(ads.length) },
    { label: "Pass spend gate", value: fmtInt(matureCount) },
    { label: "Spend in selection", value: fmtUsd(totalSpend, 0) },
    zeroDates.length > 0
      ? { label: "Billing stops", value: zeroDates[zeroDates.length - 1], warn: true }
      : { label: "Billing stops", value: "None" },
  ];
  return (
    <div className="rounded-2xl border bg-white dark:bg-card shadow-sm px-6 py-4" data-testid="section-health-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.label}>
            <div className="text-xs text-muted-foreground">{it.label}</div>
            <div className={`text-xl font-bold tabular-nums mt-0.5 ${it.warn ? "text-amber-500" : ""}`}>{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account-specific conversion funnel (Task #2525). Opt-in per brand via
// config.primaryConversionStep. The account funnel is authoritative: its
// labels/counts/costs come from the account's own tracked steps (mapping) or
// the standard set, never from the client-side ad aggregate. Every value
// preserves the tracked-zero (known/count:0) vs unavailable (unknown/count
// null) distinction; there is NEVER a generic fallback on query error.
// ---------------------------------------------------------------------------

// Render a step's count honoring the tracked-zero vs unavailable distinction.
// known && count is a real number (0 renders "0"); unknown/null renders the
// "Not configured" state (never a generic fallback).
function funnelStepCountLabel(step: CreativeFunnelStep | null): { value: string; unavailable: boolean } {
  if (!step || !step.known || step.count === null) return { value: "Not configured", unavailable: true };
  return { value: fmtInt(step.count), unavailable: false };
}

// Top scorecard when a primary conversion step is configured. The selected
// step is authoritative for the dynamic label, count, and cost-per metric.
function AccountFunnelScorecard({ step, loading, errored, spend, minSpend, minImpr, excludeBoosted, selectionAdCount }: {
  step: CreativeFunnelStep | null;
  loading: boolean; errored: boolean;
  spend: number | null; minSpend: number; minImpr: number; excludeBoosted: boolean; selectionAdCount: number;
}) {
  const gateNote = "Kaya custom-conversion totals for the selected date range.";
  const selectionTile = (
    <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
      <div className="text-xs text-muted-foreground">Ads in selection</div>
      <div className="text-xl font-bold tabular-nums mt-1" data-testid="tile-account-funnel-ads-in-selection">
        {fmtInt(selectionAdCount)}
      </div>
    </div>
  );
  if (loading) {
    return (
      <SectionCard title="Lead performance" testId="section-account-funnel-scorecard">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {selectionTile}
          <Skeleton className="h-20 w-full col-span-1 md:col-span-3" />
        </div>
      </SectionCard>
    );
  }
  // On query error there is NO generic fallback — the card says so plainly.
  if (errored || !step || spend === null) {
    return (
      <SectionCard title="Lead performance" subtitle={gateNote} testId="section-account-funnel-scorecard">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {selectionTile}
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="text-account-funnel-error">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          {errored
            ? "Kaya custom-conversion data is unavailable right now."
            : "The selected conversion is not available in Kaya's custom-conversion response. Ask an admin to review the Analytics setting."}
        </div>
      </SectionCard>
    );
  }
  const count = funnelStepCountLabel(step);
  const label = step.label;
  const costPer = step.known ? step.costPer : null;
  const tiles: Array<{ label: string; value: string; testId: string; muted?: boolean }> = [
    { label: "Ads in selection", value: fmtInt(selectionAdCount), testId: "tile-account-funnel-ads-in-selection" },
    { label: "Ad Spend", value: fmtUsd(spend, 0), testId: "tile-account-funnel-spend" },
    { label, value: count.value, testId: "tile-account-funnel-count", muted: count.unavailable },
    { label: `Cost / ${label.toLowerCase()}`, value: count.unavailable ? "—" : fmtUsd(costPer), testId: "tile-account-funnel-cost", muted: count.unavailable },
  ];
  return (
    <SectionCard
      title="Lead performance"
      subtitle={`Authoritative Kaya custom-conversion "${label}". ${gateNote} Cost per is supplied by the conversion service.`}
      testId="section-account-funnel-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.testId} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
            <div className="text-xs text-muted-foreground">{t.label}</div>
            <div className={`text-xl font-bold tabular-nums mt-1 ${t.muted ? "text-muted-foreground" : ""}`} data-testid={t.testId}>{t.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// One account-funnel column: dynamic step bars (label/count/cost from the
// feed's tracked steps), honoring the tracked-zero vs unavailable distinction.
// Used for the overall view and each
// persona group. No producer comparison is invented here.
function AccountFunnelColumn({ totals, stepOrder, primaryStep, idSuffix = "" }: {
  totals: CreativeFunnelTotals;
  stepOrder: CreativeFunnelStepKey[];
  primaryStep: CreativeFunnelStepKey;
  idSuffix?: string;
}) {
  const steps = stepOrder.map((k) => totals.steps[k]).filter((s): s is CreativeFunnelStep => !!s);
  // Bar scaling uses the first KNOWN numeric count as the top of the funnel.
  const base = steps.find((s) => s.known && s.count !== null)?.count ?? 0;
  const maxSqrt = Math.sqrt(Math.max(1, base));
  let prevKnown: number | null = null;
  return (
    <>
      <div className="space-y-3 max-w-2xl">
        {steps.map((s) => {
          const known = s.known && s.count !== null;
          const value = known ? (s.count as number) : 0;
          const width = known ? Math.max(1.5, (Math.sqrt(value) / maxSqrt) * 100) : 0;
          const conv = known && prevKnown && prevKnown > 0 ? (value / prevKnown) * 100 : null;
          prevKnown = known ? value : null;
          const isPrimary = s.key === primaryStep;
          const cnt = funnelStepCountLabel(s);
          return (
            <div key={s.key} data-testid={`account-funnel-step${idSuffix}-${s.key}`}>
              <div className="flex items-baseline justify-between text-xs mb-1 gap-2">
                <span className={`flex items-center gap-1.5 ${isPrimary ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
                  {s.label}
                </span>
                <span className={`tabular-nums font-semibold ${cnt.unavailable ? "text-muted-foreground font-normal" : ""}`}>
                  {cnt.value}
                  {conv !== null && <span className="text-muted-foreground font-normal ml-1.5">({conv >= 10 ? conv.toFixed(0) : conv.toFixed(1)}%)</span>}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-neutral-100 dark:bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${width}%`, background: isPrimary ? VIOLET : GREY }} />
              </div>
              {known && (
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Cost / {s.label.toLowerCase()}: <span className="tabular-nums font-medium">{fmtUsd(s.costPer)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// Replaces the legacy efficiency funnel when a primary conversion step is
// configured. Shows the overall eligible totals/stats/low-data, dynamic step
// labels, provenance badges, and dynamic persona groups — all sourced from the
// account funnel. Never a generic fallback on error.
function AccountFunnelSection({
  data,
  loading,
  errored,
  primaryStep,
  minSpend,
  excludeBoosted,
  selectedGroupKey,
  setSelectedGroupKey,
}: {
  data: CreativeFunnelResponse | undefined;
  loading: boolean; errored: boolean;
  primaryStep: CreativeFunnelStepKey;
  minSpend: number; excludeBoosted: boolean;
  selectedGroupKey: string | null;
  setSelectedGroupKey: (groupKey: string | null) => void;
}) {
  if (loading) {
    return (
      <SectionCard title="Conversion funnel" testId="section-account-funnel">
        <Skeleton className="h-48 w-full" />
      </SectionCard>
    );
  }
  if (errored || !data) {
    return (
      <SectionCard title="Conversion funnel" testId="section-account-funnel">
        <div className="text-sm text-muted-foreground flex items-center gap-2" data-testid="text-account-funnel-section-error">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          Conversion data is unavailable right now — this account funnel could not be loaded.
        </div>
      </SectionCard>
    );
  }
  const { stats, lowData, overall, groups, stepOrder } = data;
  const isKayaCustomConversions = data.conversionSource === "kaya_custom_conversions";
  const selectedGroup = selectedGroupKey
    ? groups.find((group) => group.key === selectedGroupKey) ?? null
    : null;
  const displayedTotals = selectedGroup?.totals ?? overall;
  return (
    <SectionCard
      title={isKayaCustomConversions ? "Kaya reservation conversions" : "Conversion funnel"}
      subtitle={isKayaCustomConversions
        ? "Custom-conversion totals for the selected date range. Reservation starts and reservations are separate metrics; pixel reservations are not included or combined."
        : `Account-wide conversion steps from summed tracked counts${excludeBoosted ? "; boosted ads excluded" : ""}. Producer comparison does not scope this endpoint. Steps show their own labels and provenance; a tracked "0" is a real zero, "Not configured" means the account does not expose that step.`}
      testId="section-account-funnel"
    >
      {isKayaCustomConversions ? (
        <div className="mb-5">
          <div className="text-xs text-muted-foreground">Reported conversion spend</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-kaya-custom-conversion-spend">
            {fmtUsd(overall.spend, 0)}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <div>
          <div className="text-xs text-muted-foreground">Eligible ads</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-funnel-eligible-ads">{fmtInt(stats.eligibleAds)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Matched ads</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-funnel-matched-ads">{fmtInt(stats.matchedAds)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Low-spend ads</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-funnel-lowspend-ads">{fmtInt(stats.lowSpendAds)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Eligible spend</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-funnel-eligible-spend">{fmtUsd(overall.spend, 0)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Eligible impressions</div>
          <div className="text-lg font-bold tabular-nums" data-testid="stat-funnel-eligible-impressions">{fmtCompact(overall.impressions)}</div>
        </div>
      </div>
      )}

      {/* Overall funnel */}
      {groups.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-1.5" data-testid="pills-funnel-groups">
            <Pill
              active={selectedGroupKey === null}
              onClick={() => setSelectedGroupKey(null)}
              testId="button-funnel-group-all"
            >
              All groups
            </Pill>
            {groups.slice(0, 8).map((group) => (
              <Pill
                key={group.key}
                active={selectedGroupKey === group.key}
                onClick={() => setSelectedGroupKey(selectedGroupKey === group.key ? null : group.key)}
                testId={`button-funnel-group-${group.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="max-w-[160px] truncate"
              >
                {group.label}
              </Pill>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            These account-defined groups scope this conversion funnel only; the trend chart keeps its ad-feed persona drill-down.
          </p>
        </div>
      )}
      <div data-testid={selectedGroup ? `account-funnel-group-${selectedGroup.key}` : "account-funnel-overall"}>
        {selectedGroup && <div className="text-sm font-semibold mb-3">{selectedGroup.label}</div>}
        <AccountFunnelColumn
          totals={displayedTotals}
          stepOrder={stepOrder}
          primaryStep={primaryStep}
          idSuffix={selectedGroup ? `-${selectedGroup.key}` : ""}
        />
      </div>

      {/* Low-data note */}
      {!isKayaCustomConversions && lowData.ads > 0 && (
        <p className="text-[11px] text-muted-foreground mt-4" data-testid="text-funnel-lowdata">
          {fmtInt(lowData.ads)} ad{lowData.ads === 1 ? "" : "s"} under the ${minSpend} spend gate
          {lowData.spend !== null && <> ({fmtUsd(lowData.spend, 0)} spend</>}
          {lowData.spend !== null && lowData.impressions !== null && <>, {fmtCompact(lowData.impressions)} impressions</>}
          {lowData.spend !== null && <>)</>} are excluded from the funnel above.
        </p>
      )}

    </SectionCard>
  );
}

function LeadGenScorecard({ agg, leadSourceField, minSpend, minImpr, excludeBoosted, selectionAdCount }: {
  agg: ReturnType<typeof aggregateAds>;
  leadSourceField: LeadSourceField;
  minSpend: number; minImpr: number; excludeBoosted: boolean; selectionAdCount: number;
}) {
  const lg = leadGenAggregate(agg, leadSourceField);
  const tiles: Array<{ label: string; value: string }> = [
    { label: "Ads in selection", value: fmtInt(selectionAdCount) },
    { label: "Ad Spend", value: fmtUsd(lg.spend, 0) },
    { label: "Leads", value: fmtInt(lg.leads) },
    { label: "CPL", value: fmtUsd(lg.cpl) },
  ];
  return (
    <SectionCard
      title="Lead performance"
      subtitle={`Values from summed counts across mature ads. Gated to ads over $${minSpend} / ${fmtCompact(minImpr)} impressions${excludeBoosted ? "; boosted ads excluded" : ""}. CPL = spend ÷ leads.`}
      testId="section-leadgen-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
            <div className="text-xs text-muted-foreground">{t.label}</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid={`tile-leadgen-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function LeadGenDateComparisonScorecard({
  agg,
  priorAgg,
  leadSourceField,
  minSpend,
  minImpr,
  excludeBoosted,
  selectionAdCount,
  currentRange,
  comparisonKind,
  priorRange,
  priorLoading,
  priorUnavailable,
}: {
  agg: ReturnType<typeof aggregateAds>;
  priorAgg: ReturnType<typeof aggregateAds>;
  leadSourceField: LeadSourceField;
  minSpend: number;
  minImpr: number;
  excludeBoosted: boolean;
  selectionAdCount: number;
  currentRange: CpDateRange;
  comparisonKind: CpDateComparison;
  priorRange: CpDateRange;
  priorLoading: boolean;
  priorUnavailable: boolean;
}) {
  const current = leadGenAggregate(agg, leadSourceField);
  const previous = leadGenAggregate(priorAgg, leadSourceField);
  const comparisonLabel = comparisonKind === "custom" ? "Comparison period" : "Previous period";
  const rows = [
    { key: "spend", label: "Ad Spend", current: current.spend, previous: previous.spend, format: (v: number | null) => fmtUsd(v, 0), betterWhen: null },
    { key: "leads", label: "Leads", current: current.leads, previous: previous.leads, format: (v: number | null) => v === null ? "—" : fmtInt(v), betterWhen: null },
    { key: "cpl", label: "CPL", current: current.cpl, previous: previous.cpl, format: (v: number | null) => fmtUsd(v), betterWhen: "lower" as const },
  ];
  return (
    <SectionCard
      title="Lead performance"
      subtitle={`Current ${fmtRangeSummary(currentRange)} vs ${comparisonLabel.toLowerCase()} ${fmtRangeSummary(priorRange)}. Same tier, producer, boosted-post, and $${minSpend} / ${fmtCompact(minImpr)} impression maturity rules${excludeBoosted ? "; boosted ads excluded" : ""}.`}
      testId="section-leadgen-date-comparison-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
          <div className="text-xs text-muted-foreground">Ads in selection</div>
          <div className="text-xl font-bold tabular-nums mt-1" data-testid="tile-leadgen-ads-in-selection">{fmtInt(selectionAdCount)}</div>
        </div>
        {rows.map((row) => (
          <div key={row.key} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
            <div className="text-xs text-muted-foreground">{row.label}</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid={`tile-leadgen-${row.key}`}>{row.format(row.current)}</div>
            <div className="text-[11px] text-muted-foreground mt-1" data-testid={`label-current-${row.key}`}>
              Current · {fmtRangeSummary(currentRange)}
            </div>
          </div>
        ))}
      </div>
      {priorLoading ? (
        <Skeleton className="h-20 w-full" data-testid="skeleton-leadgen-prior" />
      ) : priorUnavailable ? (
        <div className="rounded-xl border border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2" data-testid="text-leadgen-prior-unavailable">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Comparison data is unavailable for {fmtRangeSummary(priorRange)}. Current-period reporting is unchanged.
        </div>
      ) : (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2" data-testid="label-prior-range">
            {comparisonLabel} · {fmtRangeSummary(priorRange)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {rows.map((row) => {
              const delta = periodDelta(row.current, row.previous, row.betterWhen);
              const movement = delta.pct === null
                ? "No % baseline"
                : `${delta.pct > 0 ? "+" : ""}${delta.pct.toFixed(1)}%`;
              const tone = delta.better === true
                ? "text-emerald-600 dark:text-emerald-400"
                : delta.better === false
                  ? "text-red-600 dark:text-red-400"
                  : "text-muted-foreground";
              return (
                <div key={row.key} className="rounded-xl border px-4 py-3" data-testid={`prior-metric-${row.key}`}>
                  <div className="text-xs text-muted-foreground">{row.label}</div>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span className="font-semibold tabular-nums" data-testid={`value-prior-${row.key}`}>{row.format(row.previous)}</span>
                    <span className={`text-xs font-semibold tabular-nums ${tone}`} data-testid={`delta-prior-${row.key}`}>
                      {movement}
                      {row.key === "cpl" && delta.better !== null ? (delta.better ? " · improvement" : " · regression") : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SectionCard>
  );
}
function Scorecard({ agg, minSpend, minImpr, excludeBoosted }: {
  agg: ReturnType<typeof aggregateAds>;
  minSpend: number; minImpr: number; excludeBoosted: boolean;
}) {
  const tiles: Array<{ label: string; value: string }> = [
    { label: "Spend", value: fmtUsd(agg.spend, 0) },
    { label: "CTR", value: fmtPct(agg.ctr) },
    { label: "CPM", value: fmtUsd(agg.cpm, 0) },
    { label: "ATC / click", value: fmtPct(agg.atcPerClick, 1) },
    { label: "Cost / add to cart", value: fmtUsd(agg.costPerAtc, 0) },
    { label: "Cost / checkout", value: fmtUsd(agg.costPerCheckout, 0) },
    { label: "Cost / purchase", value: fmtUsd(agg.costPerPurchase, 0) },
  ];
  return (
    <SectionCard
      title="30-second verdict"
      subtitle={`Values from summed counts across mature ads. Gated to ads over $${minSpend} / ${fmtCompact(minImpr)} impressions${excludeBoosted ? "; boosted ads excluded" : ""}. CTR is all-click CTR.`}
      testId="section-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
            <div className="text-xs text-muted-foreground">{t.label}</div>
            <div className="text-xl font-bold tabular-nums mt-1" data-testid={`tile-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t.value}</div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 3b — Both-mode comparison scorecard: PersonaOS values with % delta vs
// in-house. Delta is green when PersonaOS is better for the metric's
// direction (higher CTR/ATC-per-click; lower CPM/cost-per-add-to-cart/
// cost-per-checkout (the checkout/Lead-stage event) / cost-per-purchase).
// Spend is context only.
// ---------------------------------------------------------------------------
const GREY = "#8B8FA3";

function fmtMetricValue(key: string, v: number | null): string {
  switch (key) {
    case "spend": return fmtUsd(v, 0);
    case "ctr": return fmtPct(v);
    case "cpm": return fmtUsd(v, 0);
    case "atcPerClick": return fmtPct(v, 1);
    case "costPerAtc": return fmtUsd(v, 0);
    case "costPerCheckout": return fmtUsd(v, 0);
    case "costPerPurchase": return fmtUsd(v, 0);
    default: return "—";
  }
}

function ComparisonScorecard({ aggs, minSpend, minImpr, excludeBoosted, tier, inhouseLabel }: {
  aggs: Record<ProducerGroup, ReturnType<typeof aggregateAds>>;
  minSpend: number; minImpr: number; excludeBoosted: boolean; tier: string; inhouseLabel: string;
}) {
  return (
    <SectionCard
      title={`PersonaOS vs. ${inhouseLabel} — 30-second verdict`}
      subtitle={`PersonaOS values with delta vs ${inhouseLabel} benchmark, ${tier} tier, from summed counts across mature ads (over $${minSpend} / ${fmtCompact(minImpr)} impressions${excludeBoosted ? "; boosted ads excluded" : ""}). CTR is all-click CTR.`}
      testId="section-comparison-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {COMPARISON_METRICS.map((m) => {
          const pv = aggs.personaos[m.key] as number | null;
          const iv = aggs.inhouse[m.key] as number | null;
          const d = producerDelta(pv, iv, m.betterWhen);
          return (
            <div key={m.key} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
              <div className="text-xs text-muted-foreground">{m.label}</div>
              <div className="text-xl font-bold tabular-nums mt-1" data-testid={`tile-compare-${m.key}`}>
                {fmtMetricValue(m.key as string, pv)}
              </div>
              <div className="text-[11px] tabular-nums mt-0.5">
                {d.pct === null ? (
                  <span className="text-muted-foreground">vs {inhouseLabel} —</span>
                ) : (
                  <span
                    className={
                      d.better === null
                        ? "text-muted-foreground"
                        : d.better
                          ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                          : "text-amber-600 dark:text-amber-400 font-semibold"
                    }
                    data-testid={`delta-compare-${m.key}`}
                  >
                    {d.pct > 0 ? "+" : ""}{d.pct.toFixed(1)}% vs {inhouseLabel}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{inhouseLabel}: {fmtMetricValue(m.key as string, iv)}</div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Cost per add to cart is the primary decision metric (lower = better). CTR is an all-click proxy — this feed has no video-view or outbound-click fields.
      </p>
    </SectionCard>
  );
}

function LeadGenComparisonScorecard({ aggs, leadSourceField, minSpend, minImpr, excludeBoosted, tier, inhouseLabel, selectionAdCount }: {
  aggs: Record<ProducerGroup, ReturnType<typeof aggregateAds>>;
  leadSourceField: LeadSourceField;
  minSpend: number;
  minImpr: number;
  excludeBoosted: boolean;
  tier: string;
  inhouseLabel: string;
  selectionAdCount: number;
}) {
  const personaos = leadGenAggregate(aggs.personaos, leadSourceField);
  const inhouse = leadGenAggregate(aggs.inhouse, leadSourceField);
  const metrics: Array<{
    key: "spend" | "leads" | "cpl";
    label: string;
    betterWhen: "higher" | "lower" | null;
    format: (value: number | null) => string;
  }> = [
    { key: "spend", label: "Ad Spend", betterWhen: null, format: (value) => fmtUsd(value, 0) },
    { key: "leads", label: "Leads", betterWhen: "higher", format: (value) => value === null ? "—" : fmtInt(value) },
    { key: "cpl", label: "CPL", betterWhen: "lower", format: (value) => fmtUsd(value) },
  ];

  return (
    <SectionCard
      title={`PersonaOS vs. ${inhouseLabel} — Lead performance`}
      subtitle={`PersonaOS values with delta vs ${inhouseLabel} benchmark, ${tier} tier, from summed counts across mature ads (over $${minSpend} / ${fmtCompact(minImpr)} impressions${excludeBoosted ? "; boosted ads excluded" : ""}). CPL = spend ÷ leads.`}
      testId="section-comparison-leadgen-scorecard"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
          <div className="text-xs text-muted-foreground">Ads in selection</div>
          <div className="text-xl font-bold tabular-nums mt-1" data-testid="tile-compare-leadgen-ads-in-selection">
            {fmtInt(selectionAdCount)}
          </div>
        </div>
        {metrics.map((metric) => {
          const personaValue = personaos[metric.key];
          const inhouseValue = inhouse[metric.key];
          const delta = producerDelta(personaValue, inhouseValue, metric.betterWhen);
          return (
            <div key={metric.key} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3.5">
              <div className="text-xs text-muted-foreground">{metric.label}</div>
              <div className="text-xl font-bold tabular-nums mt-1" data-testid={`tile-compare-leadgen-${metric.key}`}>
                {metric.format(personaValue)}
              </div>
              <div className="text-[11px] tabular-nums mt-0.5">
                {delta.pct === null ? (
                  <span className="text-muted-foreground">vs {inhouseLabel} —</span>
                ) : (
                  <span
                    className={
                      delta.better === null
                        ? "text-muted-foreground"
                        : delta.better
                          ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                          : "text-amber-600 dark:text-amber-400 font-semibold"
                    }
                    data-testid={`delta-compare-leadgen-${metric.key}`}
                  >
                    {delta.pct > 0 ? "+" : ""}{delta.pct.toFixed(1)}% vs {inhouseLabel}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {inhouseLabel}: {metric.format(inhouseValue)}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Shared persona drill-down pills — "All personas" + one pill per persona
// (mature-spend order). One selection drives the funnel AND the trend.
// ---------------------------------------------------------------------------
function PersonaDrillPills({ drillPersona, setDrillPersona, personaOptions, idPrefix }: {
  drillPersona: string | null; setDrillPersona: (p: string | null) => void;
  personaOptions: string[]; idPrefix: string;
}) {
  if (personaOptions.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`pills-${idPrefix}-personas`}>
      <Pill active={drillPersona === null} onClick={() => setDrillPersona(null)} testId={`button-${idPrefix}-persona-all`}>
        All personas
      </Pill>
      {personaOptions.slice(0, 8).map((p) => (
        <Pill
          key={p}
          active={drillPersona === p}
          onClick={() => setDrillPersona(drillPersona === p ? null : p)}
          testId={`button-${idPrefix}-persona-${p.toLowerCase()}`}
          className="max-w-[160px] truncate"
        >
          {p}
        </Pill>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4 — Efficiency funnel: overall persona performance first, drill into a
// single persona via the shared selection. Cost per add-to-cart and cost per
// complete checkout (the Lead-stage event) stay in.
// ---------------------------------------------------------------------------
// One funnel column: step bars + step conversion % + the three cost cards.
// `idSuffix` namespaces the stat testids in comparison mode ("" = the single
// funnel, keeping the historical testids byte-identical).
function FunnelBody({ agg, barColor, idSuffix = "" }: {
  agg: CreativeAggregate; barColor: string; idSuffix?: string;
}) {
  const steps = [
    { label: "Impressions", value: agg.impressions },
    { label: "Clicks", value: agg.clicks },
    { label: "Add to cart", value: agg.atc },
    { label: "Checkout", value: agg.checkout },
    { label: "Purchase", value: agg.purchases },
  ];
  const maxSqrt = Math.sqrt(Math.max(1, steps[0].value));
  return (
    <>
      <div className="space-y-3 max-w-2xl">
        {steps.map((s, i) => {
          const width = Math.max(1.5, (Math.sqrt(s.value) / maxSqrt) * 100);
          const prev = i > 0 ? steps[i - 1].value : null;
          const conv = prev && prev > 0 ? (s.value / prev) * 100 : null;
          return (
            <div key={s.label}>
              <div className="flex items-baseline justify-between text-xs mb-1">
                <span className="text-muted-foreground">{s.label}</span>
                <span className="tabular-nums font-semibold">
                  {fmtCompact(s.value)}
                  {conv !== null && <span className="text-muted-foreground font-normal ml-1.5">({conv >= 10 ? conv.toFixed(0) : conv.toFixed(1)}%)</span>}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-neutral-100 dark:bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${width}%`, background: barColor }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Cost / add to cart</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`funnel-stat-cost-atc${idSuffix}`}>{fmtUsd(agg.costPerAtc, 0)}</div>
        </div>
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Cost / checkout</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`funnel-stat-cost-checkout${idSuffix}`}>{fmtUsd(agg.costPerCheckout, 0)}</div>
        </div>
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Cost / purchase</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`funnel-stat-cost-purchase${idSuffix}`}>{fmtUsd(agg.costPerPurchase, 0)}</div>
        </div>
      </div>
    </>
  );
}

// Standard lead-gen dashboards use the configured completed-lead count rather
// than displaying empty ecommerce stages. Counts and ratios come from the same
// mature, filtered ad slice as the Lead performance scorecard.
function LeadGenFunnelBody({ agg, leadSourceField, barColor, idSuffix = "" }: {
  agg: CreativeAggregate;
  leadSourceField: LeadSourceField;
  barColor: string;
  idSuffix?: string;
}) {
  const lg = leadGenAggregate(agg, leadSourceField);
  const clickToLead = agg.clicks > 0 ? (lg.leads / agg.clicks) * 100 : null;
  const steps = [
    { key: "impressions", label: "Impressions", value: agg.impressions },
    { key: "clicks", label: "Clicks", value: agg.clicks },
    { key: "leads", label: "Leads", value: lg.leads },
  ];
  const maxSqrt = Math.sqrt(Math.max(1, steps[0].value));

  return (
    <>
      <div className="space-y-3 max-w-2xl">
        {steps.map((step, index) => {
          const width = Math.max(1.5, Math.min(100, (Math.sqrt(step.value) / maxSqrt) * 100));
          const previous = index > 0 ? steps[index - 1].value : null;
          const conversion = previous && previous > 0 ? (step.value / previous) * 100 : null;
          return (
            <div key={step.key}>
              <div className="flex items-baseline justify-between text-xs mb-1">
                <span className="text-muted-foreground">{step.label}</span>
                <span
                  className="tabular-nums font-semibold"
                  data-testid={`leadgen-funnel-step-${step.key}${idSuffix}`}
                >
                  {fmtCompact(step.value)}
                  {conversion !== null && (
                    <span className="text-muted-foreground font-normal ml-1.5">
                      ({conversion >= 10 ? conversion.toFixed(0) : conversion.toFixed(1)}%)
                    </span>
                  )}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-neutral-100 dark:bg-muted overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${width}%`, background: barColor }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-4">
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Cost / click</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`leadgen-funnel-stat-cpc${idSuffix}`}>
            {fmtUsd(agg.cpc)}
          </div>
        </div>
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Cost / lead</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`leadgen-funnel-stat-cpl${idSuffix}`}>
            {fmtUsd(lg.cpl)}
          </div>
        </div>
        <div className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-2.5">
          <div className="text-xs text-muted-foreground">Click → lead</div>
          <div className="text-lg font-bold tabular-nums" data-testid={`leadgen-funnel-stat-click-to-lead${idSuffix}`}>
            {fmtPct(clickToLead, 1)}
          </div>
        </div>
      </div>
    </>
  );
}

function LeadGenFunnelSection({
  ads,
  minSpend,
  minImpr,
  leadSourceField,
  drillPersona,
  setDrillPersona,
  personaOptions,
  comparing = false,
  inhouseLabel,
}: {
  ads: CreativeAdRow[];
  minSpend: number;
  minImpr: number;
  leadSourceField: LeadSourceField;
  drillPersona: string | null;
  setDrillPersona: (p: string | null) => void;
  personaOptions: string[];
  comparing?: boolean;
  inhouseLabel?: string;
}) {
  const agg = useMemo(() => {
    const scoped = drillPersona === null ? ads : ads.filter((ad) => ad.persona === drillPersona);
    return aggregateAds(scoped.filter((ad) => isMatureAd(ad, minSpend, minImpr)));
  }, [ads, drillPersona, minSpend, minImpr]);

  const producerFunnelAggs = useMemo(() => {
    if (!comparing) return null;
    const groups = splitAdsByProducer(ads);
    return {
      personaos: aggregateAds(groups.personaos.filter((ad) => isMatureAd(ad, minSpend, minImpr))),
      inhouse: aggregateAds(groups.inhouse.filter((ad) => isMatureAd(ad, minSpend, minImpr))),
    };
  }, [comparing, ads, minSpend, minImpr]);

  if (comparing && producerFunnelAggs) {
    const benchLabel = inhouseLabel || PRODUCER_LABELS.inhouse;
    return (
      <SectionCard
        title="Lead funnel"
        subtitle={`Step-by-step conversion from impressions to leads — ${PRODUCER_LABELS.personaos} vs. ${benchLabel}, mature ads only, summed counts per producer. Persona drill-down is unavailable while comparing producers.`}
        testId="section-comparison-leadgen-funnels"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {([
            ["personaos", PRODUCER_LABELS.personaos, VIOLET],
            ["inhouse", benchLabel, GREY],
          ] as Array<[ProducerGroup, string, string]>).map(([group, label, color]) => (
            <div key={group} data-testid={`leadgen-funnel-column-${group}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-sm font-semibold">{label}</span>
              </div>
              <LeadGenFunnelBody
                agg={producerFunnelAggs[group]}
                leadSourceField={leadSourceField}
                barColor={color}
                idSuffix={`-${group}`}
              />
            </div>
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Lead funnel"
      subtitle={drillPersona
        ? `Step-by-step conversion from impressions to leads for ${drillPersona} (mature ads, summed counts).`
        : "Step-by-step conversion from impressions to leads, all personas. Pick a persona to drill in."}
      testId="section-leadgen-funnel"
    >
      <div className="mb-4">
        <PersonaDrillPills
          drillPersona={drillPersona}
          setDrillPersona={setDrillPersona}
          personaOptions={personaOptions}
          idPrefix="leadgen-funnel"
        />
      </div>
      <LeadGenFunnelBody agg={agg} leadSourceField={leadSourceField} barColor={VIOLET} />
    </SectionCard>
  );
}

function FunnelSection({ ads, minSpend, minImpr, drillPersona, setDrillPersona, personaOptions, comparing = false, inhouseLabel }: {
  ads: CreativeAdRow[]; minSpend: number; minImpr: number;
  drillPersona: string | null; setDrillPersona: (p: string | null) => void;
  personaOptions: string[];
  // Side-by-side comparison (Task #2331): active only when the page-level
  // comparison mode is on (opt-in brand + Both producers + single tier).
  comparing?: boolean; inhouseLabel?: string;
}) {
  const agg = useMemo(() => {
    const scoped = drillPersona === null ? ads : ads.filter((a) => a.persona === drillPersona);
    return aggregateAds(scoped.filter((a) => isMatureAd(a, minSpend, minImpr)));
  }, [ads, drillPersona, minSpend, minImpr]);
  // Per-producer funnels for comparison mode — maturity gate applies WITHIN
  // each group, matching the comparison scorecard's aggregates.
  const producerFunnelAggs = useMemo(() => {
    if (!comparing) return null;
    const groups = splitAdsByProducer(ads);
    return {
      personaos: aggregateAds(groups.personaos.filter((a) => isMatureAd(a, minSpend, minImpr))),
      inhouse: aggregateAds(groups.inhouse.filter((a) => isMatureAd(a, minSpend, minImpr))),
    };
  }, [comparing, ads, minSpend, minImpr]);

  if (comparing && producerFunnelAggs) {
    const benchLabel = inhouseLabel || PRODUCER_LABELS.inhouse;
    return (
      <SectionCard
        title="Efficiency funnel"
        subtitle={`Step-by-step conversion from impression to purchase — ${PRODUCER_LABELS.personaos} vs. ${benchLabel}, mature ads only, summed counts per producer. Persona drill-down is unavailable while comparing producers.`}
        testId="section-comparison-funnels"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {([
            ["personaos", PRODUCER_LABELS.personaos, VIOLET],
            ["inhouse", benchLabel, GREY],
          ] as Array<[ProducerGroup, string, string]>).map(([group, label, color]) => (
            <div key={group} data-testid={`funnel-column-${group}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-sm font-semibold">{label}</span>
              </div>
              <FunnelBody agg={producerFunnelAggs[group]} barColor={color} idSuffix={`-${group}`} />
            </div>
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Efficiency funnel"
      subtitle={drillPersona
        ? `Step-by-step conversion for ${drillPersona} (mature ads, summed counts).`
        : "Step-by-step conversion from impression to purchase, all personas. Pick a persona to drill in."}
      testId="section-funnel"
    >
      <div className="mb-4">
        <PersonaDrillPills drillPersona={drillPersona} setDrillPersona={setDrillPersona} personaOptions={personaOptions} idPrefix="funnel" />
      </div>
      <FunnelBody agg={agg} barColor={VIOLET} />
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 5 — Persona quadrant: colored scale / fix / cut REGIONS (not bubbles), top
// 5 personas as bubbles with accessible shape + gray-shade identifiers.
// Clicking a bubble opens the persona detail view; the shared drill-down
// persona gets a violet highlight ring.
// x = CTR (right = better) · y = Cost/ATC reversed (up = cheaper = better):
//   top-right = SCALE · bottom-left = CUT · other two = FIX.
// ---------------------------------------------------------------------------
// Distinct per-persona bubble colors (user preference: colors, not shapes;
// no red/near-red — red is reserved for the CUT region tint).
const QUADRANT_COLORS = ["#7C3AED", "#2563EB", "#0D9488", "#D97706", "#DB2777", "#059669", "#DC2626", "#475569"];

const QUADRANT_OTHER_COLOR = "#9CA3AF";

// Legend key for the shared "everything without a distinct color" bucket.
const QUADRANT_OTHER_KEY = "Other personas";

// Persona-less ads belong in the quadrant medians/zones, but remain outside
// persona-scoped dashboard controls and summaries.
const QUADRANT_UNCATEGORIZED_KEY = "Uncategorized";
const QUADRANT_UNCATEGORIZED_COLOR = "#334155";

function QuadrantSection({ ads, minSpend, minImpr, personaColors, onSelect, drillPersona, hiddenNote, leadSourceField }: {
  ads: CreativeAdRow[]; minSpend: number; minImpr: number;
  personaColors: Map<string, string>; onSelect: (ad: CreativeAdRow) => void;
  drillPersona: string | null; hiddenNote: string | null;
  leadSourceField?: LeadSourceField;
}) {
  // Per-persona show/hide toggles (legend chips). Keys are persona names plus
  // the shared "Other personas" bucket and the independent Uncategorized group.
  const [hiddenPersonas, setHiddenPersonas] = useState<Set<string>>(new Set());
  const toggleLegend = (key: string) => setHiddenPersonas((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Per-ad points (user feedback: keep individual ads as data points), color
  // coded by persona via the SHARED page-level persona→color map (mature-spend
  // order, same colors as the trend lines); unmapped personas share a neutral
  // gray "Other personas" bucket. Missing personas use their own neutral group.
  const { allPoints, legend } = useMemo(() => {
    const matureAds = ads.filter((a) => isMatureAd(a, minSpend, minImpr));
    const spendByPersona = new Map<string, number>();
    for (const a of matureAds) {
      if (a.persona) {
        spendByPersona.set(a.persona, (spendByPersona.get(a.persona) ?? 0) + a.spend);
      }
    }
    const present = Array.from(spendByPersona.entries()).sort((a, b) => b[1] - a[1]).map(([p]) => p);
    const colored = present.filter((p) => personaColors.has(p)).map((p) => ({ persona: p, color: personaColors.get(p)! }));
    const hasOther = present.some((p) => !personaColors.has(p));
    const pts = matureAds
      .map((a) => {
        const agg = aggregateAds([a]);
        const lead = leadSourceField ? leadGenAggregate(agg, leadSourceField) : null;
        const persona = a.persona || QUADRANT_UNCATEGORIZED_KEY;
        const uncategorized = !a.persona;
        return {
          ad: a,
          persona,
          adName: a.adName,
          spend: a.spend,
          purchases: a.purchases,
          ctr: agg.ctr,
          costPerAtc: agg.costPerAtc,
          leads: lead?.leads ?? 0,
          cpl: lead?.cpl ?? null,
          cpc: lead?.cpc ?? null,
          clickToLead: lead?.clickToLead ?? null,
          color: uncategorized
            ? QUADRANT_UNCATEGORIZED_COLOR
            : personaColors.get(persona) ?? QUADRANT_OTHER_COLOR,
          legendKey: uncategorized
            ? QUADRANT_UNCATEGORIZED_KEY
            : personaColors.has(persona) ? persona : QUADRANT_OTHER_KEY,
        };
      })
      .filter((p) => leadSourceField ? p.cpl !== null : p.ctr !== null && p.costPerAtc !== null)
      .map((p) => ({
        ...p,
        x: leadSourceField ? p.spend : p.ctr as number,
        cost: leadSourceField ? p.cpl as number : p.costPerAtc as number,
        bubble: leadSourceField ? Math.max(p.leads, 1) : p.spend,
      }));
    const hasUncategorized = pts.some((p) => p.legendKey === QUADRANT_UNCATEGORIZED_KEY);
    return {
      allPoints: pts,
      legend: [
        ...colored,
        ...(hasOther ? [{ persona: QUADRANT_OTHER_KEY, color: QUADRANT_OTHER_COLOR }] : []),
        ...(hasUncategorized ? [{ persona: QUADRANT_UNCATEGORIZED_KEY, color: QUADRANT_UNCATEGORIZED_COLOR }] : []),
      ],
    };
  }, [ads, minSpend, minImpr, personaColors, leadSourceField]);

  // Toggled-off personas drop out of the plot entirely — zones and axis
  // domains recompute from the VISIBLE dots (the subtitle promises medians of
  // the plotted ads).
  const points = useMemo(
    () => allPoints.filter((p) => !hiddenPersonas.has(p.legendKey)),
    [allPoints, hiddenPersonas],
  );

  // Zone split at the medians of the plotted personas; padded axis domains so
  // the colored regions cover the full plot area.
  const medX = median(points.map((p) => p.x));
  const medCpa = median(points.map((p) => p.cost));
  const xMax = points.length ? Math.max(...points.map((p) => p.x)) * 1.25 : 1;
  const yMax = points.length ? Math.max(...points.map((p) => p.cost)) * 1.25 : 1;

  if (allPoints.length === 0) {
    return (
      <SectionCard title={leadSourceField ? "Lead creative quadrant" : "Ad performance quadrant — scale / fix / cut"} testId="section-quadrant">
        <p className="text-sm text-muted-foreground">No mature ads with plottable performance in the current selection.</p>
      </SectionCard>
    );
  }
  const zoneFor = (p: { x: number; cost: number }) => {
    if (medX === null || medCpa === null) return "—";
    const highX = p.x >= medX;
    const cheap = p.cost <= medCpa;
    if (leadSourceField) return highX && cheap ? "Scale" : !highX && cheap ? "Prove" : highX ? "Fix or cap" : "Watch";
    return highX && cheap ? "Scale" : !highX && !cheap ? "Cut" : "Fix";
  };
  return (
    <SectionCard
      title={leadSourceField ? "Lead creative quadrant" : "Ad performance quadrant — scale / fix / cut"}
      subtitle={leadSourceField
        ? `Spend versus cost per lead for mature ads. Bubble size represents leads; color represents persona. Medians recalculate from visible ads. Click a bubble for detail or a legend chip to hide/show a persona.${hiddenNote ? ` ${hiddenNote}` : ""}`
        : `Each mature ad plots as a bubble, color-coded by persona (top personas by spend get distinct colors — same colors as the trend lines). Green region = scale, amber = fix, red = cut. Bubble size = spend. Click a bubble to open the ad; click a legend chip to hide/show that persona's dots.${hiddenNote ? ` ${hiddenNote}` : ""}`}
      testId="section-quadrant"
    >
      <ResponsiveContainer width="100%" height={380}>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
          {/* Colored zones: with y reversed, "up" = low Cost/ATC. */}
          {medX !== null && medCpa !== null && (
            <>
              <ReferenceArea x1={medX} x2={xMax} y1={0} y2={medCpa} fill="#10B981" fillOpacity={0.09} ifOverflow="hidden" label={{ value: "SCALE", position: "insideTopRight", fontSize: 11, fill: "#047857", opacity: 0.8 }} />
              <ReferenceArea x1={0} x2={medX} y1={medCpa} y2={yMax} fill="#EF4444" fillOpacity={0.07} ifOverflow="hidden" label={{ value: leadSourceField ? "WATCH" : "CUT", position: "insideBottomLeft", fontSize: 11, fill: "#B91C1C", opacity: 0.8 }} />
              <ReferenceArea x1={0} x2={medX} y1={0} y2={medCpa} fill="#F59E0B" fillOpacity={0.07} ifOverflow="hidden" label={{ value: leadSourceField ? "PROVE" : "FIX", position: "insideTopLeft", fontSize: 11, fill: "#B45309", opacity: 0.8 }} />
              <ReferenceArea x1={medX} x2={xMax} y1={medCpa} y2={yMax} fill="#F59E0B" fillOpacity={0.07} ifOverflow="hidden" label={{ value: leadSourceField ? "FIX / CAP" : "FIX", position: "insideBottomRight", fontSize: 11, fill: "#B45309", opacity: 0.8 }} />
            </>
          )}
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis type="number" dataKey="x" name={leadSourceField ? "Spend" : "CTR"} domain={[0, xMax]} tick={{ fontSize: 11 }} tickFormatter={(v) => leadSourceField ? `$${Math.round(v)}` : `${Number(v).toFixed(1)}%`} label={{ value: leadSourceField ? "Spend" : "CTR (all-click)", position: "insideBottom", offset: -5, fontSize: 11 }} />
          <YAxis type="number" dataKey="cost" name={leadSourceField ? "CPL" : "Cost / add to cart"} reversed domain={[0, yMax]} tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v)}`} label={{ value: leadSourceField ? "Cost per lead (up = better)" : "Cost / add to cart (up = better)", angle: -90, position: "insideLeft", fontSize: 11 }} />
          <ZAxis type="number" dataKey="bubble" range={[60, 900]} name={leadSourceField ? "Leads" : "Spend"} />
          <RTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ payload }) => {
              const p = payload?.[0]?.payload;
              if (!p?.persona) return null;
              return (
                <div className="rounded-lg border bg-popover p-2.5 text-xs shadow-md max-w-[260px]">
                  <div className="font-medium break-words [overflow-wrap:anywhere]">{p.adName}</div>
                  <div className="text-muted-foreground mt-0.5">{p.persona} · {zoneFor(p)}</div>
                  {leadSourceField ? (
                    <>
                      <div className="text-muted-foreground">{fmtInt(p.leads)} leads · CPL {fmtUsd(p.cpl)} · Click → lead {fmtPct(p.clickToLead, 1)}</div>
                      <div className="text-muted-foreground">Spend {fmtUsd(p.spend, 0)} · CPC {fmtUsd(p.cpc)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-muted-foreground">CTR {p.ctr.toFixed(2)}% · Cost / add to cart {fmtUsd(p.costPerAtc)}</div>
                      <div className="text-muted-foreground">Spend {fmtUsd(p.spend, 0)} · {fmtInt(p.purchases)} purchases</div>
                    </>
                  )}
                </div>
              );
            }}
          />
          {medX !== null && <ReferenceLine x={medX} strokeDasharray="4 4" stroke="hsl(var(--border))" />}
          {medCpa !== null && <ReferenceLine y={medCpa} strokeDasharray="4 4" stroke="hsl(var(--border))" />}
          <Scatter
            data={points}
            onClick={(p: any) => p?.ad && onSelect(p.ad)}
            cursor="pointer"
            shape={(props: any) => {
              const { cx, cy, payload } = props;
              // ZAxis sizing gives us an area-scaled radius via props.size.
              const r = Math.max(5, Math.sqrt((props.size ?? 120) / Math.PI));
              const highlighted = drillPersona === payload.persona;
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={payload.color}
                  fillOpacity={0.82}
                  stroke={highlighted ? "#111827" : "#fff"}
                  strokeWidth={highlighted ? 2.5 : 1}
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelect(payload.ad)}
                  data-testid={`bubble-quadrant-${String(payload.persona).toLowerCase()}`}
                />
              );
            }}
          />
        </ScatterChart>
      </ResponsiveContainer>
      {/* Color legend — one distinct color per persona; click toggles that
          persona's dots on/off (hidden chips render struck-through + faded). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2" data-testid="legend-quadrant-personas">
        {legend.map((l) => {
          const off = hiddenPersonas.has(l.persona);
          return (
            <button
              key={l.persona}
              type="button"
              onClick={() => toggleLegend(l.persona)}
              aria-pressed={!off}
              title={off ? `Show ${l.persona}` : `Hide ${l.persona}`}
              className={`flex items-center gap-1.5 text-xs transition-opacity ${off ? "opacity-40 line-through text-foreground/60" : "text-foreground/80 hover:text-foreground"}`}
              data-testid={`legend-quadrant-${l.persona.toLowerCase()}`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                <circle cx={7} cy={7} r={5.5} fill={l.color} fillOpacity={off ? 0.35 : 1} />
              </svg>
              {l.persona}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {leadSourceField
          ? "Zones split at the plotted ads' median spend and CPL. CPL uses the configured completed-lead event for this account."
          : "Zones split at the plotted ads' median CTR / cost per add to cart. Engagement axis uses all-click CTR — video-view fields are unavailable on this feed (documented proxy)."}
      </p>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 6 + 7 — Best personas cards + persona breakdown table
// ---------------------------------------------------------------------------
const CALL_BADGE: Record<string, string> = {
  "Double down": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400",
  "Fund to validate": "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-400",
  Pause: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400",
  Iterate: "bg-neutral-100 text-neutral-600 dark:bg-muted dark:text-muted-foreground",
  Retire: "bg-amber-200 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300",
  "Try new angle": "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400",
  "Needs more data": "bg-neutral-100 text-neutral-500 dark:bg-muted dark:text-muted-foreground",
};

function PersonaAvatar({ src, name, size }: { src: string | null; name: string; size: "sm" | "md" }) {
  const px = size === "md" ? "h-7 w-7" : "h-6 w-6";
  if (!src) {
    return (
      <span className={`${px} shrink-0 rounded-full bg-violet-100 dark:bg-violet-950/60 text-violet-600 dark:text-violet-300 flex items-center justify-center text-[10px] font-semibold`} data-testid={`avatar-persona-fallback-${name.toLowerCase()}`}>
        {name.trim().charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={optimizedImg(src, 112)}
      alt={name}
      className={`${px} shrink-0 rounded-full object-cover border border-white/70 dark:border-border shadow-sm`}
      data-testid={`avatar-persona-${name.toLowerCase()}`}
    />
  );
}

function PersonaSections({ ads, minSpend, minImpr, enrichFor, onOpenPersona, personaFor, leadSourceField }: {
  ads: CreativeAdRow[]; // ALL filtered ads (incl. immature — verdicts gate internally)
  minSpend: number; minImpr: number;
  enrichFor: (adName: string) => CreativeAdEnrichment;
  onOpenPersona: (label: string) => void;
  personaFor: (label: string) => PersonaMatch | null;
  leadSourceField?: LeadSourceField;
}) {
  const personas = useMemo(() => {
    const map = new Map<string, CreativeAdRow[]>();
    for (const a of ads) {
      if (!a.persona) continue;
      const list = map.get(a.persona) || [];
      list.push(a);
      map.set(a.persona, list);
    }
    return Array.from(map.entries())
      .map(([name, list]) => {
        // Ranked metrics stay mature-only (same gating as before); the
        // weighted verdict shares the exact same helpers as the drill-down
        // so table badges and the detail banner can never contradict.
        const matureList = list.filter((a) => isMatureAd(a, minSpend, minImpr));
        const agg = aggregateAds(matureList);
        const lead = leadSourceField ? leadGenAggregate(agg, leadSourceField) : null;
        const verdict = personaVerdict(list, enrichFor, minSpend, minImpr);
        return { name, count: matureList.length, agg, lead, verdict, call: verdict.action };
      })
      .filter((p) => p.count > 0)
      .sort((a, b) => leadSourceField
        ? (b.lead?.leads ?? 0) - (a.lead?.leads ?? 0) || (a.lead?.cpl ?? Infinity) - (b.lead?.cpl ?? Infinity)
        : (a.agg.costPerAtc ?? Infinity) - (b.agg.costPerAtc ?? Infinity));
  }, [ads, minSpend, minImpr, enrichFor, leadSourceField]);

  if (personas.length === 0) return null;
  return (
    <>
      <SectionCard
        title="Best personas"
        subtitle={leadSourceField
          ? "Ranked by completed leads across mature ads carrying a persona name. CPL and click-to-lead rate come from each persona's summed spend, clicks, and leads. Click a card to drill in."
          : "Ranked by cost per add to cart (cheapest first) across mature ads carrying a persona name. Click a card to drill into that persona."}
        testId="section-best-personas"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {personas.slice(0, 10).map((p, i) => (
            <div
              key={p.name}
              className={`rounded-xl border overflow-hidden cursor-pointer transition-shadow hover:shadow-md bg-white dark:bg-card ${
                i === 0 ? "border-violet-400 ring-1 ring-violet-300 dark:ring-violet-800" : ""
              }`}
              onClick={() => onOpenPersona(p.name)}
              data-testid={`card-persona-${p.name.toLowerCase()}`}
            >
              <div className="relative">
                {(() => {
                  const personaImg = personaFor(p.name)?.imageUrl ?? null;
                  return personaImg ? (
                    <img src={optimizedImg(personaImg, 480)} alt={p.name} className="w-full aspect-[4/3] object-cover" data-testid={`img-persona-card-${p.name.toLowerCase()}`} />
                  ) : (
                    <div className={`w-full aspect-[4/3] flex items-center justify-center ${i === 0 ? "bg-violet-100/70 dark:bg-violet-950/40" : "bg-violet-50 dark:bg-muted/40"}`}>
                      <ImageIcon className="h-6 w-6 text-violet-300 dark:text-muted-foreground" />
                    </div>
                  );
                })()}
                <span className={`absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  i === 0 ? "bg-violet-600 text-white" : "bg-neutral-800/75 text-white"
                }`}>
                  #{i + 1}
                </span>
              </div>
              <div className="p-3 space-y-1">
                <div className="text-xs font-semibold leading-snug line-clamp-2" title={personaFor(p.name)?.name ?? p.name} data-testid={`text-persona-card-name-${p.name.toLowerCase()}`}>
                  {personaFor(p.name)?.name ?? p.name}
                </div>
                {leadSourceField && p.lead ? (
                  <>
                    <div className="text-lg font-bold tabular-nums leading-none pt-0.5" data-testid={`text-persona-leads-${p.name.toLowerCase()}`}>
                      {fmtInt(p.lead.leads)} <span className="text-[10px] font-medium text-muted-foreground">leads</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums" data-testid={`text-persona-cpl-${p.name.toLowerCase()}`}>
                      CPL {fmtUsd(p.lead.cpl)} · Click → lead {fmtPct(p.lead.clickToLead, 1)}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {p.count} ads · {fmtUsd(p.agg.spend, 0)} spend · CPC {fmtUsd(p.lead.cpc)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold tabular-nums leading-none pt-0.5">
                      {fmtUsd(p.agg.costPerAtc, 0)} <span className="text-[10px] font-medium text-muted-foreground">/ add to cart</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {p.count} ads · {fmtUsd(p.agg.spend, 0)} · CTR {fmtPct(p.agg.ctr)}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      {fmtInt(p.agg.purchases)} purchases · ATC/click {fmtPct(p.agg.atcPerClick, 1)}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Persona breakdown"
        subtitle={leadSourceField
          ? "Lead outcomes by persona from mature ads. Green CPL beats the selection median. Click a row to inspect its angles and creatives."
          : "Which personas to double down on. Green cost per add to cart beats the selection median. Click a row to drill into the persona."}
        testId="section-persona-table"
      >
        <PersonaTable personas={personas} personaFor={personaFor} onOpenPersona={onOpenPersona} leadSourceField={leadSourceField} />
      </SectionCard>
    </>
  );
}

function PersonaTable({ personas, personaFor, onOpenPersona, leadSourceField }: {
  personas: Array<{ name: string; count: number; agg: ReturnType<typeof aggregateAds>; lead: ReturnType<typeof leadGenAggregate> | null; call: string; verdict: PersonaVerdict }>;
  personaFor: (label: string) => PersonaMatch | null;
  onOpenPersona: (label: string) => void;
  leadSourceField?: LeadSourceField;
}) {
  const medCpa = median(personas
    .map((p) => leadSourceField ? p.lead?.cpl ?? null : p.agg.costPerAtc)
    .filter((v): v is number => v !== null));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground border-b">
            <th className="text-left font-normal py-2 pr-3">Persona</th>
            <th className="text-right font-normal py-2 px-3">Ads</th>
            <th className="text-right font-normal py-2 px-3">Spend</th>
            <th className="text-right font-normal py-2 px-3">{leadSourceField ? "Leads" : "Purchases"}</th>
            <th className="text-right font-normal py-2 px-3">{leadSourceField ? "Click → lead" : "ATC/click"}</th>
            <th className="text-right font-normal py-2 px-3">{leadSourceField ? "CPL" : "Cost / add to cart"}</th>
            {!leadSourceField && <th className="text-right font-normal py-2 pl-3">Call</th>}
          </tr>
        </thead>
        <tbody>
          {personas.map((p) => {
            const cpa = leadSourceField ? p.lead?.cpl ?? null : p.agg.costPerAtc;
            const beats = cpa !== null && medCpa !== null && cpa < medCpa;
            return (
              <tr key={p.name} className="border-b last:border-0 cursor-pointer hover:bg-neutral-50 dark:hover:bg-muted/30" onClick={() => onOpenPersona(p.name)} data-testid={`row-persona-${p.name.toLowerCase()}`}>
                <td className="py-2.5 pr-3 font-medium">
                  <span className="flex items-center gap-2">
                    <PersonaAvatar src={personaFor(p.name)?.imageUrl ?? null} name={p.name} size="md" />
                    {personaFor(p.name)?.name ?? p.name}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtInt(p.count)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtUsd(p.agg.spend, 0)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtInt(leadSourceField ? p.lead?.leads ?? 0 : p.agg.purchases)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtPct(leadSourceField ? p.lead?.clickToLead ?? null : p.agg.atcPerClick, 1)}</td>
                <td className={`py-2.5 px-3 text-right tabular-nums font-semibold ${beats ? "text-emerald-600 dark:text-emerald-400" : ""}`} data-testid={`${leadSourceField ? "text-cpl" : "text-cost-atc"}-${p.name.toLowerCase()}`}>
                  {fmtUsd(cpa, 0)}
                </td>
                {!leadSourceField && <td className="py-2.5 pl-3 text-right">
                  <span
                    className={`inline-block text-[10px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${CALL_BADGE[p.call] || CALL_BADGE.Iterate}`}
                    title={p.verdict.rationale}
                    data-testid={`badge-call-${p.name.toLowerCase()}`}
                  >
                    {p.call}
                  </span>
                </td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8 — Trend (account-appropriate outcome/efficiency, amber zero-spend markers)
// ---------------------------------------------------------------------------
type TrendMetric = "costPerAtc" | "ctr" | "leads" | "cpl" | "clickToLead" | "spend";
type TrendRow = {
  date: string;
  spend: number;
  clicks: number;
  impressions: number;
  atc: number;
  checkout?: number;
  purchases?: number;
  leads?: number;
};

function trendLeadCount(row: TrendRow, field: LeadSourceField): number {
  return Number(row[field] ?? 0) || 0;
}

function TrendSection({ trend, loading, brandId, since, until, tier, excludeBoosted, producerMode, minSpend, minImpr, drillPersona, setDrillPersona, personaOptions, personaColors, leadSourceField }: {
  trend: TrendRow[];
  loading: boolean;
  brandId: string;
  since: string; until: string; tier: string; excludeBoosted: boolean;
  producerMode: ProducerGroup | "both";
  minSpend: number; minImpr: number;
  drillPersona: string | null; setDrillPersona: (p: string | null) => void;
  personaOptions: string[]; personaColors: Map<string, string>;
  leadSourceField?: LeadSourceField;
}) {
  const [metric, setMetric] = useState<TrendMetric>(leadSourceField ? "leads" : "costPerAtc");
  useEffect(() => {
    setMetric(leadSourceField ? "leads" : "costPerAtc");
  }, [leadSourceField]);
  // Per-persona drill-down: the daily feed has no persona dimension, so the
  // server rebuilds a persona series from per-day /creative/ads snapshots.
  // producerMode rides along so persona series honor the same producer scope
  // as every other section on the page.
  const personaQs = new URLSearchParams({
    since, until, tier, excludeBoosted: String(excludeBoosted), producerMode,
    minSpend: String(minSpend), minImpressions: String(minImpr), persona: drillPersona ?? "",
  }).toString();
  const personaTrendQuery = useQuery<TrendRow[]>({
    ...creativeFeedQueryOptions<TrendRow[]>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/persona-trend?${personaQs}`],
    enabled: Boolean(brandId) && drillPersona !== null,
    staleTime: 5 * 60 * 1000,
  });
  const drilled = drillPersona !== null;
  // All-personas mode plots one line per persona — same per-day snapshot
  // rebuild, but the server returns a flat {date, persona, ...} breakdown.
  const allQs = new URLSearchParams({
    since, until, tier, excludeBoosted: String(excludeBoosted), producerMode,
    minSpend: String(minSpend), minImpressions: String(minImpr), all: "true",
  }).toString();
  const allTrendQuery = useQuery<Array<TrendRow & { persona: string }>>({
    ...creativeFeedQueryOptions<Array<TrendRow & { persona: string }>>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/persona-trend?${allQs}`],
    enabled: Boolean(brandId) && !drilled && personaOptions.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  // Standard lead mode also rebuilds the account-wide series from daily ad
  // rows. Unlike the upstream aggregate trend, this path can apply the exact
  // same selected-range maturity gate as every lead dashboard section.
  const overallQs = new URLSearchParams({
    since, until, tier, excludeBoosted: String(excludeBoosted), producerMode,
    minSpend: String(minSpend), minImpressions: String(minImpr), overall: "true",
  }).toString();
  const overallTrendQuery = useQuery<TrendRow[]>({
    ...creativeFeedQueryOptions<TrendRow[]>(),
    queryKey: [`/api/brands/${brandId}/creative-performance/persona-trend?${overallQs}`],
    enabled: Boolean(brandId) && Boolean(leadSourceField) && !drilled,
    staleTime: 5 * 60 * 1000,
  });
  const source = drilled
    ? (personaTrendQuery.data ?? [])
    : leadSourceField
      ? (overallTrendQuery.data ?? [])
      : trend;
  // Daily ratios come from per-date sums — never averaged across rows.
  const data = useMemo(() => source.map((t) => {
    const leads = leadSourceField ? trendLeadCount(t, leadSourceField) : 0;
    return {
      date: t.date.slice(5),
      fullDate: t.date,
      spend: t.spend,
      leads,
      cpl: leads > 0 ? t.spend / leads : null,
      clickToLead: t.clicks > 0 ? (leads / t.clicks) * 100 : null,
      costPerAtc: t.atc > 0 ? t.spend / t.atc : null,
      ctr: t.impressions > 0 ? (t.clicks / t.impressions) * 100 : null,
    };
  }), [source, leadSourceField]);
  const valueFor = (row: TrendRow): number | null => {
    const leads = leadSourceField ? trendLeadCount(row, leadSourceField) : 0;
    if (metric === "leads") return leads;
    if (metric === "cpl") return leads > 0 ? row.spend / leads : null;
    if (metric === "clickToLead") return row.clicks > 0 ? (leads / row.clicks) * 100 : null;
    if (metric === "spend") return row.spend;
    if (metric === "costPerAtc") return row.atc > 0 ? row.spend / row.atc : null;
    return row.impressions > 0 ? (row.clicks / row.impressions) * 100 : null;
  };
  const isCurrency = metric === "costPerAtc" || metric === "cpl" || metric === "spend";
  const isPercent = metric === "ctr" || metric === "clickToLead";
  const metricLabel: Record<TrendMetric, string> = {
    costPerAtc: "Cost / add to cart",
    ctr: "CTR",
    leads: "Leads",
    cpl: "CPL",
    clickToLead: "Click → lead",
    spend: "Spend",
  };
  // Personas that get their own line — the same top set as the drill pills,
  // colored via the shared page-level persona→color map.
  const linePersonas = useMemo(() => personaOptions.slice(0, 8), [personaOptions]);
  const multiLine = !drilled && linePersonas.length > 0 && (allTrendQuery.data?.length ?? 0) > 0;
  // One row per date, one key per persona holding the current metric's value.
  // Dates come from the aggregate trend (full window incl. zero-spend days);
  // spend stays account-wide so the zero-spend markers keep their meaning.
  const multiData = useMemo(() => {
    if (!multiLine) return [];
    const perDate = new Map<string, Record<string, number | null>>();
    for (const r of allTrendQuery.data ?? []) {
      if (!linePersonas.includes(r.persona)) continue;
      const rec = perDate.get(r.date) ?? {};
      rec[r.persona] = valueFor(r);
      perDate.set(r.date, rec);
    }
    const dates = data.length > 0
      ? data.map((d) => ({ date: d.date, fullDate: d.fullDate, spend: d.spend }))
      : Array.from(perDate.keys()).sort().map((d) => ({ date: d.slice(5), fullDate: d, spend: 0 }));
    return dates.map((d) => ({ ...d, ...(perDate.get(d.fullDate) ?? {}) }));
  }, [multiLine, allTrendQuery.data, linePersonas, metric, leadSourceField, data]);
  // Zero-spend markers only make sense account-wide; a persona simply not
  // running that day isn't a billing stop.
  const zeroDays = drilled ? [] : data.filter((d) => d.spend === 0);
  const busy =
    (!leadSourceField && loading) ||
    (drilled && personaTrendQuery.isLoading) ||
    (!drilled && Boolean(leadSourceField) && overallTrendQuery.isLoading) ||
    (!drilled && personaOptions.length > 0 && allTrendQuery.isLoading);
  const leadTrendErrored = Boolean(leadSourceField) && (
    drilled
      ? personaTrendQuery.isError && !personaTrendQuery.data
      : (overallTrendQuery.isError && !overallTrendQuery.data) || (personaOptions.length > 0 && allTrendQuery.isError && !allTrendQuery.data)
  );
  return (
    <SectionCard
      title="Are we improving?"
      subtitle={drilled
        ? `Daily ${metricLabel[metric].toLowerCase()} for ${drillPersona}.`
        : `Daily ${metricLabel[metric].toLowerCase()} — one line per persona. Pick a persona to drill in.`}
      testId="section-trend"
      headerRight={
        <div className="flex items-center rounded-full border bg-white dark:bg-card p-0.5">
          {(leadSourceField
            ? (["leads", "cpl", "clickToLead", "spend"] as TrendMetric[])
            : (["costPerAtc", "ctr"] as TrendMetric[])
          ).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setMetric(key)}
              data-testid={key === "costPerAtc"
                ? "button-trend-cost-atc"
                : `button-trend-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`}
              className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${metric === key ? "bg-violet-600 text-white" : "text-foreground/70"}`}
            >
              {metricLabel[key]}
            </button>
          ))}
        </div>
      }
    >
      <div className="mb-4">
        <PersonaDrillPills drillPersona={drillPersona} setDrillPersona={setDrillPersona} personaOptions={personaOptions} idPrefix="trend" />
      </div>
      {busy ? <Skeleton className="h-64 w-full" /> : leadTrendErrored ? (
        <p className="text-sm text-destructive flex items-center gap-2" data-testid="text-lead-trend-error">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Completed lead data couldn't be loaded for this trend. No ecommerce metric has been substituted.
        </p>
      ) : drilled && personaTrendQuery.isError ? (
        <p className="text-sm text-destructive" data-testid="text-persona-trend-error">Couldn't load the {drillPersona} trend — try again or switch back to All personas.</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={multiLine ? multiData : data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => isCurrency ? `$${v}` : isPercent ? `${v}%` : fmtCompact(Number(v))} />
              <RTooltip
                formatter={(v: any, name: any) => [
                  isCurrency
                    ? fmtUsd(typeof v === "number" ? v : null)
                    : isPercent
                      ? fmtPct(typeof v === "number" ? v : null)
                      : fmtInt(typeof v === "number" ? v : 0),
                  name,
                ]}
                labelFormatter={(l, p) => (p?.[0]?.payload?.fullDate as string) || l}
              />
              {zeroDays.map((d) => (
                <ReferenceLine key={d.fullDate} x={d.date} stroke="#f59e0b" strokeDasharray="2 4" opacity={0.7} />
              ))}
              {multiLine ? (
                linePersonas.map((p) => (
                  <Line key={p} type="monotone" name={p} dataKey={p} stroke={personaColors.get(p) ?? QUADRANT_OTHER_COLOR} strokeWidth={2} dot={false} connectNulls />
                ))
              ) : (
                <Line type="monotone" name={drilled ? (drillPersona as string) : metricLabel[metric]} dataKey={metric} stroke={drilled ? (personaColors.get(drillPersona as string) ?? VIOLET) : VIOLET} strokeWidth={2} dot={false} connectNulls={!leadSourceField} />
              )}
            </LineChart>
          </ResponsiveContainer>
          {multiLine && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2" data-testid="legend-trend-personas">
              {linePersonas.map((p) => (
                <span key={p} className="flex items-center gap-1.5 text-xs text-foreground/80" data-testid={`legend-trend-${p.toLowerCase()}`}>
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                    <rect x={1} y={5.5} width={12} height={3} rx={1.5} fill={personaColors.get(p) ?? QUADRANT_OTHER_COLOR} />
                  </svg>
                  {p}
                </span>
              ))}
            </div>
          )}
          {multiLine && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="text-trend-multiline-caveat">
              Persona lines are rebuilt from daily ad snapshots (the raw daily feed has no persona field), so days before the snapshot window may be missing.
            </p>
          )}
          {drilled && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="text-trend-persona-caveat">
              Persona series is rebuilt from daily ad snapshots (the raw daily feed has no persona field), so days before the snapshot window may be missing.
            </p>
          )}
          {zeroDays.length > 0 && (
            <p className="text-xs text-amber-500 mt-1.5">
              Zero-spend day(s) inside the flight: {zeroDays.map((d) => d.fullDate).join(", ")} — likely billing or delivery stops.
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 10 — Gallery (sortable creative cards, click → detail)
// ---------------------------------------------------------------------------
function GallerySection({ ads, minSpend, minImpr, sort, setSort, onSelect, inhouseLabel, hiddenNote, leadSourceField }: {
  ads: CreativeAdRow[]; minSpend: number; minImpr: number;
  sort: "spend" | "costPerAtc" | "leads" | "cpl"; setSort: (s: "spend" | "costPerAtc" | "leads" | "cpl") => void;
  onSelect: (ad: CreativeAdRow) => void; inhouseLabel: string; hiddenNote: string | null;
  leadSourceField?: LeadSourceField;
}) {
  const PAGE_SIZE = 24;
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => [...ads].sort((a, b) => {
    if (leadSourceField) {
      const maturityDelta = Number(isMatureAd(b, minSpend, minImpr)) - Number(isMatureAd(a, minSpend, minImpr));
      if (maturityDelta !== 0) return maturityDelta;
      const aLead = leadGenAggregate(aggregateAds([a]), leadSourceField);
      const bLead = leadGenAggregate(aggregateAds([b]), leadSourceField);
      if (sort === "leads") return bLead.leads - aLead.leads || (aLead.cpl ?? Infinity) - (bLead.cpl ?? Infinity);
      if (sort === "cpl") return (aLead.cpl ?? Infinity) - (bLead.cpl ?? Infinity);
      return b.spend - a.spend;
    }
    if (sort === "spend") return b.spend - a.spend;
    const ca = a.atc > 0 ? a.spend / a.atc : Infinity;
    const cb = b.atc > 0 ? b.spend / b.atc : Infinity;
    return ca - cb;
  }), [ads, sort, leadSourceField, minSpend, minImpr]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1); // clamp when filters shrink the list
  const pageAds = sorted.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  return (
    <SectionCard
      title="Creative gallery"
      subtitle={hiddenNote ? `The actual creative — click any card for full detail. ${hiddenNote}` : "The actual creative — click any card for full detail."}
      testId="section-gallery"
      headerRight={
        <div className="flex items-center rounded-full border bg-white dark:bg-card p-0.5">
          {leadSourceField && (
            <button type="button" onClick={() => { setSort("leads"); setPage(0); }} data-testid="button-sort-leads"
              className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${sort === "leads" ? "bg-violet-600 text-white" : "text-foreground/70"}`}>
              Most leads
            </button>
          )}
          {leadSourceField && (
            <button type="button" onClick={() => { setSort("cpl"); setPage(0); }} data-testid="button-sort-cpl"
              className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${sort === "cpl" ? "bg-violet-600 text-white" : "text-foreground/70"}`}>
              Lowest CPL
            </button>
          )}
          <button type="button" onClick={() => { setSort("spend"); setPage(0); }} data-testid="button-sort-spend"
            className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${sort === "spend" ? "bg-violet-600 text-white" : "text-foreground/70"}`}>
            {leadSourceField ? "Most spend" : "By spend"}
          </button>
          {!leadSourceField && (
            <button type="button" onClick={() => { setSort("costPerAtc"); setPage(0); }} data-testid="button-sort-cost-atc"
              className={`h-6 px-3 rounded-full text-xs font-medium transition-colors ${sort === "costPerAtc" ? "bg-violet-600 text-white" : "text-foreground/70"}`}>
              By cost / add to cart
            </button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {pageAds.map((a) => {
          const mature = isMatureAd(a, minSpend, minImpr);
          const cpa = a.atc > 0 ? a.spend / a.atc : null;
          const ctr = a.impressions > 0 ? (a.clicks / a.impressions) * 100 : null;
          const lead = leadSourceField ? leadGenAggregate(aggregateAds([a]), leadSourceField) : null;
          return (
            <div
              key={a.adId}
              className={`rounded-xl border overflow-hidden cursor-pointer bg-white dark:bg-card transition-shadow hover:shadow-md ${mature ? "" : "opacity-45"}`}
              onClick={() => onSelect(a)}
              data-testid={`card-gallery-ad-${a.adId}`}
            >
              <AdThumb ad={a} />
              <div className="p-2.5 space-y-1">
                <div className="flex items-center gap-1.5 text-[10px] flex-wrap">
                  <span
                    className="px-1.5 py-0.5 rounded-full font-medium text-white"
                    style={{ background: classifyProducer(a) === "personaos" ? VIOLET : GREY }}
                    data-testid={`badge-producer-${a.adId}`}
                  >
                    {classifyProducer(a) === "personaos" ? PRODUCER_LABELS.personaos : inhouseLabel}
                  </span>
                  {a.persona && (
                    <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 font-medium truncate max-w-[70px]">{a.persona}</span>
                  )}
                  <span className="text-muted-foreground flex items-center gap-0.5">
                    {a.format === "Video" && <VideoIcon className="h-3 w-3" />}
                    {a.format}
                  </span>
                </div>
                <div className="text-[11px] font-medium truncate">{a.adName}</div>
                {lead ? (
                  <>
                    <div className="text-[10px] font-semibold tabular-nums" data-testid={`text-gallery-leads-${a.adId}`}>
                      {fmtInt(lead.leads)} leads · CPL {fmtUsd(lead.cpl)}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums">
                      Click → lead {fmtPct(lead.clickToLead, 1)} · CPC {fmtUsd(lead.cpc)} · {fmtUsd(a.spend, 0)}
                    </div>
                  </>
                ) : (
                  <div className="text-[10px] text-muted-foreground tabular-nums">
                    {fmtUsd(a.spend, 0)}{ctr !== null ? ` · CTR ${ctr.toFixed(2)}%` : ""} · {cpa !== null ? `${fmtUsd(cpa, 0)} / add to cart` : "no ATC"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {pageCount > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="gallery-pager">
          <div className="text-xs text-muted-foreground tabular-nums">
            {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sorted.length)} of {sorted.length} ads
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} data-testid="button-gallery-prev">
              Previous
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">Page {safePage + 1} / {pageCount}</span>
            <Button size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} data-testid="button-gallery-next">
              Next
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// Gallery card thumbnail. Walks the safe feed fallback chain:
// creativeImageUrl → thumbnailUrl → imageUrl → format placeholder.
//
// Advance to the next step on load error as well as absence. Each tile
// owns its own index state, so a broken image on one card never affects others.
function AdThumb({ ad }: { ad: CreativeAdRow }) {
  // DEDUPED: the feed often returns identical imageUrl and thumbnailUrl; if a
  // duplicate stayed in the chain, advancing to it would leave the DOM src
  // unchanged, so no new error event ever fires and the broken image sticks.
  const srcs = creativeAdDisplayUrls(ad);
  // The fallback index is keyed to the URL list itself: when a data refresh
  // delivers fresh (re-signed) feed URLs, the chain restarts from the top
  // instead of staying stuck past the end from the previous stale URLs.
  const chainKey = srcs.join("|");
  const [chain, setChain] = useState({ key: chainKey, idx: 0 });
  const idx = chain.key === chainKey ? chain.idx : 0;
  const setIdx = (updater: (prev: number) => number) =>
    setChain({ key: chainKey, idx: updater(idx) });
  const src = srcs[idx];
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="w-full aspect-square object-cover"
        loading="lazy"
        onError={() => {
          // Advance to the next URL; if already at the last one, step past
          // the end so src becomes undefined and the placeholder renders.
          setIdx((prev) => prev + 1);
        }}
      />
    );
  }
  const kind = ad.format === "Video" ? "Video"
    : /^gif\b/i.test(ad.adName) ? "GIF"
    : /^existingigpost\b/i.test(ad.adName) ? "IG post"
    : "No preview";
  return (
    <div className="w-full aspect-square bg-violet-50 dark:bg-muted/40 flex flex-col items-center justify-center gap-1.5" data-testid={`placeholder-gallery-ad-${ad.adId}`}>
      {kind === "Video" ? (
        <VideoIcon className="h-6 w-6 text-violet-300 dark:text-muted-foreground" />
      ) : (
        <ImageIcon className="h-6 w-6 text-violet-300 dark:text-muted-foreground" />
      )}
      <span className="text-[10px] font-medium text-violet-400 dark:text-muted-foreground">{kind}</span>
      {ad.product && (
        <span className="text-[10px] text-muted-foreground px-2 text-center line-clamp-2">{ad.product}</span>
      )}
    </div>
  );
}

const VERDICT_BANNER: Record<string, string> = {
  "Double down": "border-emerald-300/70 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300",
  "Fund to validate": "border-sky-300/70 bg-sky-50 dark:bg-sky-950/30 text-sky-800 dark:text-sky-300",
  // Task #2254 quick fix: negative verdicts render amber/neutral, not red —
  // red banners read like errors when a card is clicked.
  Retire: "border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300",
  Pause: "border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300",
  "Try new angle": "border-amber-300/70 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300",
  Iterate: "border-border bg-neutral-50 dark:bg-muted/30 text-foreground",
  "Needs more data": "border-border bg-neutral-50 dark:bg-muted/30 text-muted-foreground",
};
// Reference layout (attached_assets/Screenshot_2026-08-11_at_9.34.42_AM_*):
// wide sectioned modal — header (thumb + title + chips + meta), KPI strip,
// performance funnel, detailed-performance row, collapsible campaign details.
const TIER_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Cold: Snowflake, Warm: Flame, Retargeting: RefreshCw, Legacy: Archive,
};
const fmtDateShort = (s: string) => {
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
function CreativeMatchCandidateRow({
  candidate,
  confidence,
  pending,
  onUse,
}: {
  candidate: CreativeAdMatchCandidate;
  confidence?: CreativeAdMatchSuggestion["confidence"];
  pending: boolean;
  onUse: (candidate: CreativeAdMatchCandidate) => void;
}) {
  const image = candidate.imageUrl
    ? `/api/img-proxy?url=${encodeURIComponent(candidate.imageUrl)}&w=160`
    : null;
  const rawName = candidate.name?.trim() || "";
  const useContextualTitle = !rawName || /^untitled ad$/i.test(rawName) || rawName.length > 62;
  const title = useContextualTitle
    ? candidate.product || candidate.angle || candidate.persona || rawName || "Saved creative"
    : rawName;
  const metadata = [candidate.persona, candidate.product, candidate.angle]
    .filter((value) => Boolean(value) && value !== title)
    .join(" · ");
  const libraryMetadata = [
    candidate.projectName ? `Project: ${candidate.projectName}` : null,
    candidate.createdAt ? `Created ${fmtDateShort(candidate.createdAt.slice(0, 10))}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <div
      className="group flex min-w-0 items-center gap-3 rounded-xl border border-border/70 bg-card p-2.5 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50/40 dark:hover:border-violet-700 dark:hover:bg-violet-950/20"
      data-testid={`match-candidate-${candidate.id}`}
    >
      {image ? (
        <img src={image} alt="" className="h-20 w-20 rounded-lg object-cover border border-border/50 shrink-0" />
      ) : (
        <div className="h-20 w-20 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 flex-1 self-stretch py-0.5">
        <div className="flex items-start gap-1.5">
          <div className="line-clamp-2 text-sm font-semibold leading-5" title={rawName || title}>{title}</div>
          {confidence && (
            <span className="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium capitalize text-violet-700 dark:bg-violet-950/60 dark:text-violet-300">
              {confidence} confidence
            </span>
          )}
        </div>
        <div className="mt-1 line-clamp-2 text-xs leading-4 text-muted-foreground" title={metadata}>
          {metadata || "Saved in My Ads"}
        </div>
        {libraryMetadata && (
          <div className="mt-0.5 truncate text-[11px] leading-4 text-muted-foreground" title={libraryMetadata}>
            {libraryMetadata}
          </div>
        )}
      </div>
      <Button
        size="sm"
        className="shrink-0"
        disabled={pending}
        onClick={() => onUse(candidate)}
        data-testid={`button-use-match-${candidate.id}`}
      >
        {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Match"}
      </Button>
    </div>
  );
}

function MatchSourceAd({ ad }: { ad: CreativeAdRow }) {
  const imageSources = creativeAdDisplayUrls(ad);
  const sourceKey = imageSources.join("|");
  const [imageState, setImageState] = useState({ key: sourceKey, index: 0 });
  const imageIndex = imageState.key === sourceKey ? imageState.index : 0;
  const imageUrl = imageSources[imageIndex];
  const metadata = [ad.persona, ad.product, ad.format].filter(Boolean).join(" · ");
  const runDate = ad.firstDate && ad.lastDate
    ? ad.firstDate === ad.lastDate
      ? `Ran ${fmtDateShort(ad.firstDate)}`
      : `Ran ${fmtDateShort(ad.firstDate)} – ${fmtDateShort(ad.lastDate)}`
    : null;
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-violet-200/80 bg-violet-50/60 p-3 dark:border-violet-900/70 dark:bg-violet-950/20" data-testid={`match-source-ad-${ad.adId}`}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-16 w-16 shrink-0 rounded-lg border border-violet-200/70 object-cover dark:border-violet-800/70"
          onError={() => setImageState({ key: sourceKey, index: imageIndex + 1 })}
          data-testid={`image-match-source-${ad.adId}`}
        />
      ) : (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-950/60 dark:text-violet-300">
          {ad.format === "Video"
            ? <VideoIcon className="h-5 w-5" />
            : <ImageIcon className="h-5 w-5" />}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">Performance ad to match</div>
        <div className="mt-0.5 truncate text-sm font-semibold" title={ad.adName}>{ad.adName}</div>
        {metadata && <div className="mt-0.5 truncate text-xs text-muted-foreground">{metadata}</div>}
        {runDate && <div className="mt-0.5 truncate text-xs text-muted-foreground">{runDate}</div>}
      </div>
    </div>
  );
}

function CreativeMatchPanel({ brandId, ad }: { brandId: string; ad: CreativeAdRow }) {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [matchPage, setMatchPage] = useState(1);
  const [personaFilter, setPersonaFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [angleFilter, setAngleFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const prepareIterationSource = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/brands/${brandId}/creative-performance/iteration-sources`, {
        token: ad.iterationSourceToken,
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.message || "Could not prepare this creative");
      return body.source as { id: string };
    },
    onSuccess: (source) => setLocation(creativeIterationExternalSeedHref(brandId, source.id)),
  });
  const canIterate = Boolean(ad.iterationSourceToken && ad.iterationMediaType);
  const iterationUnavailable = Boolean(ad.iterationMediaType && !ad.iterationSourceToken);
  useEffect(() => {
    setPickerOpen(false);
    setSearch("");
    setMatchPage(1);
    setPersonaFilter("");
    setProductFilter("");
    setAngleFilter("");
    setProjectFilter("");
    setCreatedFrom("");
    setCreatedTo("");
  }, [ad.adId]);

  const base = `/api/brands/${brandId}/creative-performance/ads/${encodeURIComponent(ad.adId)}`;
  const suggestionParams = new URLSearchParams({
    adName: ad.adName || "",
    persona: ad.persona || "",
    product: ad.product || "",
  });
  const suggestionsQuery = useQuery<CreativeAdMatchSuggestion[]>({
    queryKey: [`${base}/match-suggestions?${suggestionParams.toString()}`],
    enabled: pickerOpen,
    staleTime: 60_000,
  });
  const candidateParams = new URLSearchParams({
    q: debouncedSearch, page: String(matchPage), persona: personaFilter, product: productFilter,
    angle: angleFilter, projectId: projectFilter, createdFrom, createdTo,
  });
  const candidatesQuery = useQuery<{ items: CreativeAdMatchCandidate[]; total: number; page: number; pageSize: number }>({
    queryKey: [`${base}/match-candidates?${candidateParams.toString()}`],
    enabled: pickerOpen,
    staleTime: 60_000,
  });
  const optionsQuery = useQuery<{
    personas: string[]; products: string[]; angles: string[];
    projects: { id: string; name: string }[];
  }>({
    queryKey: [`${base}/match-filter-options`],
    enabled: pickerOpen,
    staleTime: 60_000,
  });
  const clearFilters = () => {
    setPersonaFilter("");
    setProductFilter("");
    setAngleFilter("");
    setProjectFilter("");
    setCreatedFrom("");
    setCreatedTo("");
    setMatchPage(1);
  };
  const refreshAttribution = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string"
          && query.queryKey[0].startsWith(`/api/brands/${brandId}/creative-performance/ads?`),
      }),
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/creative-performance/ad-links`] }),
    ]);
  };
  const saveMatch = useMutation({
    mutationFn: async (candidate: CreativeAdMatchCandidate) => {
      await apiRequest("POST", `${base}/match`, { generatedAdId: candidate.id });
    },
    onSuccess: async () => {
      setPickerOpen(false);
      await refreshAttribution();
    },
  });
  const clearMatch = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `${base}/match`);
    },
    onSuccess: refreshAttribution,
  });
  const mutationError = saveMatch.error || clearMatch.error;
  const isAccessDenied = (error: unknown) => error instanceof Error && /^403:/.test(error.message);
  const browseAccessDenied = [
    suggestionsQuery.error,
    candidatesQuery.error,
    optionsQuery.error,
  ].some(isAccessDenied);
  const accessMessage = "You don't have access to Creative Performance for this brand.";
  const isSearching = Boolean(search.trim());
  const searchPending = debouncedSearch !== search.trim();
  const filterOptions = optionsQuery.data ?? { personas: [], products: [], angles: [], projects: [] };
  const projectOptions = filterOptions.projects;
  const hasActiveFilter = Boolean(personaFilter || productFilter || angleFilter || projectFilter || createdFrom || createdTo);
  const totalMatchPages = Math.max(1, Math.ceil((candidatesQuery.data?.total ?? 0) / (candidatesQuery.data?.pageSize ?? 12)));
  const displayedCandidates = candidatesQuery.data?.items ?? [];
  useEffect(() => {
    setMatchPage(1);
  }, [search, personaFilter, productFilter, angleFilter, projectFilter, createdFrom, createdTo]);

  return (
    <>
      <div className="rounded-xl border border-violet-200/70 bg-violet-50/60 p-4 dark:border-violet-900/70 dark:bg-violet-950/20" data-testid="panel-current-match">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {ad.localAd?.imageUrl && (
              <img
                src={`/api/img-proxy?url=${encodeURIComponent(ad.localAd.imageUrl)}&w=120`}
                alt=""
                className="h-14 w-14 shrink-0 rounded-lg border border-border/50 object-cover"
                data-testid="image-current-match"
              />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Link2 className="h-4 w-4 text-violet-600" />
                {ad.localAd ? "Matched My Ads creative" : "No My Ads creative linked"}
              </div>
              {ad.localAd ? (
                <>
                  <div className="mt-1 text-sm font-medium truncate" data-testid="text-current-match-name">{ad.localAd.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {ad.attributionSource === "manual" ? "Manually confirmed" : "Matched automatically by exact ad name"}
                    {[ad.localAd.persona, ad.localAd.product, ad.localAd.angle].filter(Boolean).length
                      ? ` · ${[ad.localAd.persona, ad.localAd.product, ad.localAd.angle].filter(Boolean).join(" · ")}`
                      : ""}
                  </div>
                </>
              ) : (
                <div className="mt-1 text-xs text-muted-foreground">
                  Choose the real saved creative to use its image and attribution.
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canIterate && (
              <Button
                size="sm"
                variant="outline"
                disabled={prepareIterationSource.isPending}
                onClick={() => prepareIterationSource.mutate()}
                data-testid={`button-iterate-performance-ad-${ad.adId}`}
              >
                {prepareIterationSource.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing…</> : "Iterate from this ad"}
              </Button>
            )}
            {iterationUnavailable && (
              <Button
                size="sm"
                variant="outline"
                disabled
                data-testid={`button-iterate-performance-ad-${ad.adId}`}
              >
                Iterate unavailable
              </Button>
            )}
            {ad.localAd && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setLocation(`/brands/${brandId}/library?adId=${encodeURIComponent(ad.localAd!.id)}`)
                }
                data-testid={`button-open-match-in-my-ads-${ad.adId}`}
              >
                Open in My Ads
              </Button>
            )}
            {ad.attributionSource === "manual" && (
              <Button
                size="sm"
                variant="outline"
                disabled={clearMatch.isPending}
                onClick={() => clearMatch.mutate()}
                data-testid={`button-clear-match-${ad.adId}`}
              >
                {clearMatch.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear"}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setPickerOpen(true)}
              data-testid={ad.localAd ? `button-change-match-${ad.adId}` : `button-match-ad-${ad.adId}`}
            >
              {ad.localAd ? "Change match" : "Match to My Ads"}
            </Button>
          </div>
        </div>
        {mutationError && (
          <div className="mt-2 text-xs text-red-600" role="alert">
            {isAccessDenied(mutationError) ? accessMessage : mutationError instanceof Error ? mutationError.message : "Could not update the match"}
          </div>
        )}
        {prepareIterationSource.error && (
          <div className="mt-2 text-xs text-red-600" role="alert">
            {prepareIterationSource.error instanceof Error ? prepareIterationSource.error.message : "Could not prepare this creative. Try again."}
          </div>
        )}
        {iterationUnavailable && ad.iterationUnavailableReason && (
          <div className="mt-2 text-xs text-amber-700" role="status">
            {ad.iterationUnavailableReason}
          </div>
        )}
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent
          className="flex max-w-3xl flex-col overflow-hidden p-0 sm:max-h-[88vh]"
          data-testid="dialog-ad-match-picker"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            searchInputRef.current?.focus();
          }}
        >
          <DialogHeader className="shrink-0">
            <div className="border-b bg-muted/20 px-6 pb-4 pt-6">
              <DialogTitle>Match this ad to My Ads</DialogTitle>
              <DialogDescription>
                Search your saved library and choose the exact creative behind this performance ad.
              </DialogDescription>
              <div className="mt-3">
                <MatchSourceAd ad={ad} />
              </div>
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {saveMatch.error && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
                {isAccessDenied(saveMatch.error) ? accessMessage : saveMatch.error instanceof Error ? saveMatch.error.message : "Could not update the match"}
              </div>
            )}
            {!isSearching && suggestionsQuery.data?.length ? (
              <section>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <Sparkles className="h-4 w-4 text-violet-600" /> Best matches for this ad
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {suggestionsQuery.data.slice(0, 4).map((candidate) => (
                    <CreativeMatchCandidateRow
                      key={candidate.id}
                      candidate={candidate}
                      confidence={candidate.confidence}
                      pending={saveMatch.isPending}
                      onUse={(selected) => saveMatch.mutate(selected)}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            <section data-testid={`match-library-results-${ad.adId}`}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">
                    My Ads
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Browsing your full saved library
                  </div>
                </div>
                {!searchPending && !candidatesQuery.isLoading && !candidatesQuery.isFetching && !candidatesQuery.isError && (
                  <span className="shrink-0 text-xs text-muted-foreground">{candidatesQuery.data?.total ?? 0} result{candidatesQuery.data?.total === 1 ? "" : "s"}</span>
                )}
              </div>
              <div className="relative mb-4">
                <Input
                  ref={searchInputRef}
                  type="text"
                  autoComplete="off"
                  aria-label="Search saved My Ads creatives"
                  value={search}
                  onChange={(event) => {
                    const nextSearch = event.target.value.slice(0, 120);
                    setSearch(nextSearch);
                    setMatchPage(1);
                  }}
                  placeholder="Search saved ads or text"
                  className="h-11 bg-background px-3 text-sm shadow-sm"
                  data-testid={`input-match-search-${ad.adId}`}
                />
              </div>
              {optionsQuery.isError && !browseAccessDenied && (
                <div className="mb-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
                  Filter choices could not load. You can still browse and search My Ads.{" "}
                  <Button variant="ghost" className="h-auto p-0 text-inherit underline" onClick={() => optionsQuery.refetch()}>
                    Retry filters
                  </Button>
                </div>
              )}
              <div className="mb-4 rounded-xl border bg-muted/20 p-3" data-testid="match-filters">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                      <SlidersHorizontal className="h-3.5 w-3.5" /> Filter saved ads
                    </div>
                    {hasActiveFilter && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={clearFilters}
                        data-testid={`button-clear-match-filters-${ad.adId}`}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <select
                      value={personaFilter}
                      onChange={(event) => setPersonaFilter(event.target.value)}
                      className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
                      aria-label="Filter My Ads by persona"
                      data-testid={`select-match-persona-${ad.adId}`}
                    >
                      <option value="">All personas</option>
                      {personaFilter && !filterOptions.personas.includes(personaFilter) && <option value={personaFilter}>{personaFilter}</option>}
                      {filterOptions.personas.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                    <select
                      value={productFilter}
                      onChange={(event) => setProductFilter(event.target.value)}
                      className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
                      aria-label="Filter My Ads by product"
                      data-testid={`select-match-product-${ad.adId}`}
                    >
                      <option value="">All products</option>
                      {productFilter && !filterOptions.products.includes(productFilter) && <option value={productFilter}>{productFilter}</option>}
                      {filterOptions.products.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                    <select
                      value={angleFilter}
                      onChange={(event) => setAngleFilter(event.target.value)}
                      className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
                      aria-label="Filter My Ads by angle"
                      data-testid={`select-match-angle-${ad.adId}`}
                    >
                      <option value="">All angles</option>
                      {angleFilter && !filterOptions.angles.includes(angleFilter) && <option value={angleFilter}>{angleFilter}</option>}
                      {filterOptions.angles.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    <select
                      value={projectFilter}
                      onChange={(event) => setProjectFilter(event.target.value)}
                      className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
                      aria-label="Filter My Ads by project"
                      data-testid={`select-match-project-${ad.adId}`}
                    >
                      <option value="">All projects</option>
                      {projectFilter && !projectOptions.some((project) => project.id === projectFilter) && (
                        <option value={projectFilter}>Selected project</option>
                      )}
                      {projectOptions.map((project) => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                    <label className="flex h-9 min-w-0 items-center gap-2 rounded-md border bg-background px-2 text-xs text-muted-foreground">
                      <span className="shrink-0">Created from</span>
                      <input
                        type="date"
                        value={createdFrom}
                        max={createdTo || undefined}
                        onChange={(event) => setCreatedFrom(event.target.value)}
                        aria-label="Filter My Ads created from"
                        className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                        data-testid={`input-match-created-from-${ad.adId}`}
                      />
                    </label>
                    <label className="flex h-9 min-w-0 items-center gap-2 rounded-md border bg-background px-2 text-xs text-muted-foreground">
                      <span className="shrink-0">Created to</span>
                      <input
                        type="date"
                        value={createdTo}
                        min={createdFrom || undefined}
                        onChange={(event) => setCreatedTo(event.target.value)}
                        aria-label="Filter My Ads created to"
                        className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                        data-testid={`input-match-created-to-${ad.adId}`}
                      />
                    </label>
                  </div>
              </div>
              {searchPending || candidatesQuery.isLoading || candidatesQuery.isFetching ? (
                <div className="grid gap-2 md:grid-cols-2" data-testid="match-search-loading">
                  {[0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-[102px] rounded-xl" />)}
                </div>
              ) : candidatesQuery.isError || browseAccessDenied ? (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
                  {browseAccessDenied ? accessMessage : "Could not load My Ads. Try again in a moment."}
                  {!browseAccessDenied && (
                    <Button variant="ghost" className="ml-2 h-auto p-0 text-inherit underline" onClick={() => candidatesQuery.refetch()}>
                      Retry ads
                    </Button>
                  )}
                </div>
              ) : displayedCandidates.length ? (
                <>
                  <div className="grid gap-2 md:grid-cols-2">
                    {displayedCandidates.map((candidate) => (
                      <CreativeMatchCandidateRow
                        key={candidate.id}
                        candidate={candidate}
                        pending={saveMatch.isPending}
                        onUse={(selected) => saveMatch.mutate(selected)}
                      />
                    ))}
                  </div>
                  {totalMatchPages > 1 && (
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        Page {matchPage} of {totalMatchPages}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={matchPage === 1}
                          onClick={() => setMatchPage((page) => Math.max(1, page - 1))}
                          aria-label="Previous My Ads page"
                          data-testid={`button-previous-match-page-${ad.adId}`}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={matchPage === totalMatchPages}
                          onClick={() => setMatchPage((page) => Math.min(totalMatchPages, page + 1))}
                          aria-label="Next My Ads page"
                          data-testid={`button-next-match-page-${ad.adId}`}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : matchPage > totalMatchPages ? (
                <div className="rounded-xl border border-dashed bg-muted/20 p-5 text-sm text-muted-foreground">
                  This page is no longer available.{" "}
                  <Button variant="ghost" className="h-auto p-0 underline" onClick={() => setMatchPage(1)}>
                    Return to first page
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed bg-muted/20 p-5 text-sm text-muted-foreground">
                  {isSearching || hasActiveFilter ? "No results" : "No saved My Ads creatives are available yet."}
                </div>
              )}
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdDetailDialog({ brandId, ad, onClose, inhouseLabel, enrichFor, leadSourceField, sourceHistory }: {
  brandId: string; ad: CreativeAdRow | null; onClose: () => void; inhouseLabel: string;
  enrichFor: (adName: string) => CreativeAdEnrichment;
  leadSourceField?: LeadSourceField;
  sourceHistory?: boolean;
}) {
  const [detailsOpen, setDetailsOpen] = useState(true);
  // Safe feed image chain: creativeImageUrl → thumbnailUrl → imageUrl → placeholder.
  // Represented as an ordered URL array + an index so advancing on load error
  // is a single setIdx call and stale broken-state can't carry over to a new ad.
  // Deduped like AdThumb: the feed often returns identical imageUrl and
  // thumbnailUrl — a duplicate chain step never re-fires onError (same DOM src).
  const dialogUrls = ad ? creativeAdDisplayUrls(ad) : [];
  const [dialogIdx, setDialogIdx] = useState(0);
  // Reset to the first URL whenever a new ad is opened (adId) AND whenever the
  // URL list itself changes (a data refresh can deliver fresh re-signed feed
  // URLs for the same ad — the chain must restart instead of staying exhausted).
  const dialogChainKey = dialogUrls.join("|");
  useEffect(() => {
    setDialogIdx(0);
  }, [ad?.adId, dialogChainKey]);
  if (!ad) return null;
  const agg = aggregateAds([ad]);
  const lead = leadSourceField ? leadGenAggregate(agg, leadSourceField) : null;
  const angle = ad.attributedAngle || enrichFor(ad.adName).angle;
  const producerLabel = classifyProducer(ad) === "personaos" ? PRODUCER_LABELS.personaos : inhouseLabel;
  const TierIcon = TIER_ICONS[ad.tier] ?? Snowflake;
  const FormatIcon = ad.format === "Video" ? VideoIcon : ImageIcon;
  // Checkout-per-ATC isn't in the shared aggregate — derive locally.
  const checkoutPerAtc = ad.atc > 0 ? (ad.checkout / ad.atc) * 100 : null;
  const activeRange = `${fmtDateShort(ad.firstDate)} – ${fmtDateShort(ad.lastDate)}`;

  const kpis = lead ? [
    { label: "Leads", value: fmtInt(lead.leads), sub: "Completed leads", icon: CheckCircle2, iconCls: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400" },
    { label: "CPL", value: fmtUsd(lead.cpl), sub: "Spend ÷ leads", icon: Target, iconCls: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400" },
    { label: "Click → lead", value: fmtPct(lead.clickToLead, 1), sub: "Leads ÷ clicks", icon: TrendingUp, iconCls: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400" },
    { label: "Spend", value: fmtUsd(ad.spend, 0), sub: "Total", icon: DollarSign, iconCls: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400" },
  ] : [
    { label: "Spend", value: fmtUsd(ad.spend, 0), sub: "Total", icon: DollarSign, iconCls: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400" },
    { label: "Purchases", value: fmtInt(ad.purchases), sub: "Total", icon: ShoppingCart, iconCls: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400" },
    { label: "Revenue", value: fmtUsd(ad.revenue, 0), sub: "Total", icon: TrendingUp, iconCls: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400" },
    { label: "ROAS", value: agg.roas === null ? "—" : agg.roas.toFixed(2), sub: "Return on ad spend", icon: Target, iconCls: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400" },
  ];
  const funnel = lead ? [
    { label: "Impressions", value: fmtCompact(ad.impressions), sub: null as string | null, icon: Eye },
    { label: "Clicks", value: fmtCompact(ad.clicks), sub: `CTR ${fmtPct(agg.ctr)}`, icon: MousePointerClick },
    { label: "Leads", value: fmtCompact(lead.leads), sub: `Click → lead ${fmtPct(lead.clickToLead, 1)}`, icon: CheckCircle2 },
  ] : [
    { label: "Impressions", value: fmtCompact(ad.impressions), sub: null as string | null, icon: Eye },
    { label: "Clicks", value: fmtCompact(ad.clicks), sub: `CTR ${fmtPct(agg.ctr)}`, icon: MousePointerClick },
    { label: "ATC", value: fmtCompact(ad.atc), sub: `ATC / click ${fmtPct(agg.atcPerClick, 1)}`, icon: ShoppingBag },
    { label: "Checkout", value: fmtCompact(ad.checkout), sub: `Checkout / ATC ${fmtPct(checkoutPerAtc, 1)}`, icon: CreditCard },
    { label: "Purchases", value: fmtCompact(ad.purchases), sub: `Purch / click ${fmtPct(agg.purchaseCvr)}`, icon: CheckCircle2 },
  ];
  const detailed = lead ? [
    { label: "Cost / click", value: fmtUsd(lead.cpc), icon: DollarSign },
    { label: "Cost / lead", value: fmtUsd(lead.cpl), icon: Target },
    { label: "Click → lead", value: fmtPct(lead.clickToLead, 1), icon: TrendingUp },
    { label: "CTR (all-click)", value: fmtPct(agg.ctr), icon: MousePointerClick },
    { label: "CPM", value: fmtUsd(agg.cpm), icon: DollarSign },
  ] : [
    { label: "CPM", value: fmtUsd(agg.cpm), icon: DollarSign },
    { label: "CTR (all-click)", value: fmtPct(agg.ctr), icon: MousePointerClick },
    { label: "ATC / click", value: fmtPct(agg.atcPerClick, 1), icon: ShoppingBag },
    { label: "Cost / add to cart", value: fmtUsd(agg.costPerAtc), icon: DollarSign },
    { label: "Cost / checkout", value: fmtUsd(agg.costPerCheckout), icon: CreditCard },
    { label: "Cost / purchase", value: fmtUsd(agg.costPerPurchase), icon: DollarSign },
  ];
  // Task #2254: surface every metadata field the feed/enrichment exposes —
  // angle, reach, and the raw campaign/ad ids.
  const campaignRows: Array<[string, string]> = [
    ["Campaign", ad.campaign], ["Tier", ad.tier], ["Format", ad.format],
    ["Producer", producerLabel], ["Persona", ad.persona || "—"], ["Angle", angle || "—"],
    ["Batch", ad.batch || "—"], ["Product", ad.product], ["Active", activeRange],
    ["Boosted", ad.isBoosted ? "Yes" : "No"], ["Reach", fmtInt(ad.reach)],
    ["Campaign ID", ad.campaignId || "—"], ["Ad ID", ad.adId || "—"],
  ];
  const chip = (cls: string) =>
    `inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium ${cls}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] rounded-2xl p-0 gap-0 max-h-[90vh] overflow-y-auto" data-testid="dialog-ad-detail">
        {/* min-w-0 + overflow-hidden: keep long unbroken ad names (underscored
            Meta names) from expanding the grid item past the dialog width. */}
        <div className="p-6 space-y-5 min-w-0 max-w-full overflow-hidden">
          {/* Header */}
          <DialogHeader className="space-y-0">
            <div className="flex gap-5 items-start pr-8">
              {dialogIdx < dialogUrls.length ? (
                <img
                  src={dialogUrls[dialogIdx]}
                  alt=""
                  className="w-36 h-36 sm:w-44 sm:h-44 object-cover rounded-xl shrink-0 border border-border/50"
                  onError={() => setDialogIdx((prev) => prev + 1)}
                />
              ) : (
                <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-xl shrink-0 border border-border/50 bg-muted flex items-center justify-center text-muted-foreground">
                  <FormatIcon className="h-8 w-8" />
                </div>
              )}
              <div className="min-w-0 flex-1 space-y-3">
                <DialogTitle className="text-xl sm:text-2xl font-bold leading-snug break-words [overflow-wrap:anywhere]">{ad.adName}</DialogTitle>
                <DialogDescription className="sr-only">
                  Performance details and My Ads attribution for this creative.
                </DialogDescription>
                {sourceHistory && (
                  <p className="text-xs text-muted-foreground" data-testid="source-history-period">
                    Original source ad · Metrics cover all available dates and tiers, not the dashboard's current filters.
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  <span className={chip("bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300")} data-testid="chip-tier">
                    <TierIcon className="h-3.5 w-3.5" />{ad.tier}
                  </span>
                  <span className={chip("bg-muted text-foreground/80")} data-testid="chip-format">
                    <FormatIcon className="h-3.5 w-3.5" />{ad.format}
                  </span>
                  <span className={chip("bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300")} data-testid="chip-producer">
                    <UserRound className="h-3.5 w-3.5" />{producerLabel}
                  </span>
                  <span className={chip("bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300")} data-testid="chip-product">
                    <Tag className="h-3.5 w-3.5" />{ad.product}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Batch {ad.batch || "—"}</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />Active {activeRange}</span>
                  <span className="inline-flex items-center gap-1.5"><Megaphone className="h-3.5 w-3.5" />Boosted: {ad.isBoosted ? "Yes" : "No"}</span>
                </div>
              </div>
            </div>
          </DialogHeader>

          <CreativeMatchPanel brandId={brandId} ad={ad} />

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 rounded-xl border border-border/60 divide-x divide-border/60 divide-y lg:divide-y-0 overflow-hidden">
            {kpis.map((k) => (
              <div key={k.label} className="flex items-start gap-3 p-4" data-testid={`kpi-${k.label.toLowerCase()}`}>
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${k.iconCls}`}>
                  <k.icon className="h-[18px] w-[18px]" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">{k.label}</div>
                  <div className="text-xl font-bold tabular-nums leading-tight">{k.value}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{k.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Performance funnel */}
          <div className="rounded-xl border border-border/60 p-4">
            <div className="text-sm font-semibold mb-3">{lead ? "Lead funnel" : "Performance funnel"}</div>
            <div className="flex items-stretch gap-1.5 overflow-x-auto">
              {funnel.map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <div className="self-center shrink-0 text-muted-foreground/60"><ArrowRight className="h-4 w-4" /></div>}
                  <div className="flex-1 min-w-[110px] rounded-lg border border-border/60 p-3 flex flex-col items-center text-center gap-1" data-testid={`funnel-${s.label.toLowerCase()}`}>
                    <div className="h-8 w-8 rounded-full bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400 flex items-center justify-center">
                      <s.icon className="h-4 w-4" />
                    </div>
                    <div className="text-base font-bold tabular-nums leading-tight">{s.value}</div>
                    <div className="text-[11px] text-muted-foreground">{s.label}</div>
                    {s.sub && <div className="text-[10px] text-muted-foreground/80 tabular-nums">{s.sub}</div>}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Detailed performance */}
          <div className="rounded-xl border border-border/60 p-4">
            <div className="text-sm font-semibold mb-3">Detailed performance</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-3 divide-x divide-border/40">
              {detailed.map((m) => (
                <div key={m.label} className="flex items-center gap-2.5 px-3 first:pl-0" data-testid={`detail-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                    <m.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] text-muted-foreground leading-tight">{m.label}</div>
                    <div className="text-sm font-bold tabular-nums">{m.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Campaign details (collapsible, open by default) */}
          <div className="rounded-xl border border-border/60">
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 text-left"
              onClick={() => setDetailsOpen((v) => !v)}
              data-testid="button-campaign-details-toggle"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold">
                <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />Campaign details
              </span>
              <ChevronUp className={`h-4 w-4 text-muted-foreground transition-transform ${detailsOpen ? "" : "rotate-180"}`} />
            </button>
            {detailsOpen && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                {campaignRows.map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <div className="text-[11px] text-muted-foreground mb-0.5">{k}</div>
                    <div className="text-sm break-words">{v}</div>
                  </div>
                ))}
                <div className="min-w-0">
                  <div className="text-[11px] text-muted-foreground mb-0.5">Meta campaign</div>
                  {ad.campaignAdsManagerUrl ? (
                    <a
                      href={ad.campaignAdsManagerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300"
                      data-testid={`link-meta-campaign-${ad.adId}`}
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      Open in Meta Ads Manager
                    </a>
                  ) : (
                    <div className="text-sm text-muted-foreground" data-testid={`text-meta-campaign-unavailable-${ad.adId}`}>
                      Link unavailable
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PersonaDetailView({ personaLabel, ads, minSpend, minImpr, enrichFor, angleDetailFor, personaFor, onBack, onSelect, leadSourceField }: {
  personaLabel: string;
  ads: CreativeAdRow[]; // this persona's ads under the active dashboard filters
  minSpend: number; minImpr: number;
  enrichFor: (adName: string) => CreativeAdEnrichment;
  angleDetailFor: (angleName: string) => CreativeAngleDetail | null;
  personaFor: (label: string) => PersonaMatch | null;
  onBack: () => void;
  onSelect: (ad: CreativeAdRow) => void;
  leadSourceField?: LeadSourceField;
}) {
  const [angleFilter, setAngleFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Expanded angle rows in the breakdown table (Task #2193) — details render
  // in a companion row under the angle's metrics row.
  const [expandedAngles, setExpandedAngles] = useState<Set<string>>(() => new Set());
  const toggleAngle = (angle: string) => setExpandedAngles((prev) => {
    const next = new Set(prev);
    if (next.has(angle)) next.delete(angle); else next.add(angle);
    return next;
  });
  const match = personaFor(personaLabel);
  const verdict = useMemo(
    () => personaVerdict(ads, enrichFor, minSpend, minImpr),
    [ads, enrichFor, minSpend, minImpr],
  );
  const matureAds = useMemo(() => ads.filter((a) => isMatureAd(a, minSpend, minImpr)), [ads, minSpend, minImpr]);
  const agg = useMemo(() => aggregateAds(matureAds), [matureAds]);
  const lead = leadSourceField ? leadGenAggregate(agg, leadSourceField) : null;
  const angleOf = (a: CreativeAdRow) => a.attributedAngle || enrichFor(a.adName).angle || UNKNOWN_ANGLE;
  const listAds = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ads
      .filter((a) => (angleFilter === null || angleOf(a) === angleFilter))
      .filter((a) => !q || a.adName.toLowerCase().includes(q) || a.campaign.toLowerCase().includes(q))
      .sort((x, y) => y.spend - x.spend);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, angleFilter, search, enrichFor]);

  const tiles: Array<{ label: string; value: string }> = lead ? [
    { label: "Leads", value: fmtInt(lead.leads) },
    { label: "CPL", value: fmtUsd(lead.cpl) },
    { label: "Click → lead", value: fmtPct(lead.clickToLead, 1) },
    { label: "Spend (mature)", value: fmtUsd(agg.spend, 0) },
    { label: "Clicks", value: fmtInt(agg.clicks) },
    { label: "CPC", value: fmtUsd(lead.cpc) },
  ] : [
    { label: "Spend (mature)", value: fmtUsd(agg.spend, 0) },
    { label: "Ads", value: `${fmtInt(ads.length)} (${fmtInt(matureAds.length)} mature)` },
    { label: "CTR", value: fmtPct(agg.ctr) },
    { label: "Cost / add to cart", value: fmtUsd(agg.costPerAtc, 0) },
    { label: "Purchases", value: fmtInt(agg.purchases) },
    { label: "ROAS", value: agg.roas === null ? "—" : agg.roas.toFixed(2) },
  ];

  return (
    <div className="space-y-5" data-testid="section-persona-detail">
      {/* Header: back + persona identity + aggregates */}
      <div className="rounded-2xl border bg-white dark:bg-card shadow-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" className="rounded-full" onClick={onBack} data-testid="button-persona-back">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Dashboard
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {match?.imageUrl ? (
            <img src={optimizedImg(match.imageUrl, 240)} alt={match.name} className="h-16 w-16 rounded-full object-cover border shadow-sm" data-testid="img-persona-detail-avatar" />
          ) : (
            <span className="h-16 w-16 rounded-full bg-violet-100 dark:bg-violet-950/60 text-violet-600 dark:text-violet-300 flex items-center justify-center text-xl font-semibold">
              {personaLabel.trim().charAt(0).toUpperCase()}
            </span>
          )}
          <div>
            <h2 className="text-xl font-bold tracking-tight" data-testid="text-persona-detail-name">{match?.name ?? personaLabel}</h2>
            <p className="text-xs text-muted-foreground">
              Feed label "{personaLabel}" · {verdict.iterations} iteration{verdict.iterations === 1 ? "" : "s"} (batches / generation runs) · metrics from mature ads only
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-xl border bg-neutral-50/80 dark:bg-muted/30 px-4 py-3">
              <div className="text-xs text-muted-foreground">{t.label}</div>
              <div className="text-lg font-bold tabular-nums mt-0.5" data-testid={`tile-persona-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendation banner */}
      {!leadSourceField && <div
        className={`rounded-2xl border px-6 py-4 flex flex-wrap items-start gap-3 ${VERDICT_BANNER[verdict.action] || VERDICT_BANNER.Iterate}`}
        data-testid="banner-persona-verdict"
      >
        <span className={`inline-block text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${CALL_BADGE[verdict.action] || CALL_BADGE.Iterate}`} data-testid="badge-persona-verdict">
          {verdict.action}
        </span>
        <p className="text-sm flex-1 min-w-[240px]" data-testid="text-persona-verdict-rationale">{verdict.rationale}</p>
      </div>}

      {/* Angle breakdown */}
      <SectionCard
        title="Angle breakdown"
        subtitle={leadSourceField
          ? "Completed leads and CPL by angle from mature ads (summed counts). Ads we can't match to a local generation are grouped as Unknown angle."
          : "Was it the angle or the persona? Per-angle metrics from mature ads (summed counts). Ads we can't match to a local generation are grouped as Unknown angle."}
        testId="section-persona-angles"
      >
        {verdict.angleSplits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ads for this persona under the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left font-normal py-2 pr-3">Angle</th>
                  <th className="text-right font-normal py-2 px-3">Ads</th>
                  <th className="text-right font-normal py-2 px-3">Mature</th>
                  <th className="text-right font-normal py-2 px-3">Spend</th>
                  <th className="text-right font-normal py-2 px-3">{leadSourceField ? "Clicks" : "CTR"}</th>
                  <th className="text-right font-normal py-2 px-3">{leadSourceField ? "Leads" : "Purchases"}</th>
                  <th className="text-right font-normal py-2 px-3">{leadSourceField ? "Click → lead" : "Cost / add to cart"}</th>
                  <th className="text-right font-normal py-2 pl-3">{leadSourceField ? "CPL" : "Call"}</th>
                </tr>
              </thead>
              <tbody>
                {verdict.angleSplits.map((s) => {
                  const angleLead = leadSourceField ? leadGenAggregate(s.agg, leadSourceField) : null;
                  const detail = s.known ? angleDetailFor(s.angle) : null;
                  const angleKey = s.angle.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const expanded = detail !== null && expandedAngles.has(s.angle);
                  return (
                  <React.Fragment key={s.angle}>
                  <tr className={`border-b last:border-0 ${detail ? "cursor-pointer hover:bg-neutral-50 dark:hover:bg-muted/30" : ""}`} onClick={detail ? () => toggleAngle(s.angle) : undefined} data-testid={`row-angle-${angleKey}`}>
                    <td className="py-2.5 pr-3 font-medium max-w-[280px]">
                      <span className={`flex items-center gap-1.5 ${s.known ? "" : "text-muted-foreground"}`}>
                        {detail ? (
                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-violet-400 transition-transform ${expanded ? "rotate-90" : ""}`} data-testid={`button-angle-expand-${angleKey}`} />
                        ) : (
                          <Layers className="h-3.5 w-3.5 shrink-0 text-violet-400" />
                        )}
                        <span className="truncate" title={s.angle}>{s.angle}</span>
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{fmtInt(s.ads.length)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{fmtInt(s.matureAds.length)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{fmtUsd(s.agg.spend, 0)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{leadSourceField ? fmtInt(s.agg.clicks) : fmtPct(s.agg.ctr)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums">{fmtInt(angleLead ? angleLead.leads : s.agg.purchases)}</td>
                    <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{angleLead ? fmtPct(angleLead.clickToLead, 1) : fmtUsd(s.agg.costPerAtc, 0)}</td>
                    <td className="py-2.5 pl-3 text-right">
                      {angleLead ? (
                        <span className="font-semibold tabular-nums">{fmtUsd(angleLead.cpl)}</span>
                      ) : s.call ? (
                        <span className={`inline-block text-[10px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${CALL_BADGE[s.call] || CALL_BADGE.Iterate}`}>{s.call}</span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">low data</span>
                      )}
                    </td>
                  </tr>
                  {expanded && detail && (
                    <tr className="border-b last:border-0 bg-violet-50/50 dark:bg-violet-950/20" data-testid={`row-angle-detail-${angleKey}`}>
                      <td colSpan={8} className="py-3 px-4">
                        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-xs">
                          {(detail.family || detail.subtype) && (
                            <div className="sm:col-span-2 flex items-center gap-1.5">
                              {detail.family && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 font-medium">{detail.family}</span>}
                              {detail.subtype && <span className="px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-muted text-muted-foreground font-medium">{detail.subtype}</span>}
                            </div>
                          )}
                          {detail.summary && (
                            <div className="sm:col-span-2">
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Summary</div>
                              <p className="text-foreground/90">{detail.summary}</p>
                            </div>
                          )}
                          {detail.coreTension && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Core tension</div>
                              <p className="text-foreground/90">{detail.coreTension}</p>
                            </div>
                          )}
                          {detail.beliefShift && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Belief shift</div>
                              <p className="text-foreground/90">{detail.beliefShift}</p>
                            </div>
                          )}
                          {detail.promise && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Promise</div>
                              <p className="text-foreground/90">{detail.promise}</p>
                            </div>
                          )}
                          {detail.proofAnchors.length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">Proof anchors</div>
                              <ul className="list-disc pl-4 space-y-0.5 text-foreground/90">
                                {detail.proofAnchors.map((p, i) => <li key={i}>{p}</li>)}
                              </ul>
                            </div>
                          )}
                          {!detail.summary && !detail.coreTension && !detail.beliefShift && !detail.promise && detail.proofAnchors.length === 0 && (
                            <p className="text-muted-foreground sm:col-span-2">No narrative details stored for this angle.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Ad list */}
      <SectionCard
        title={`All ads for ${match?.name ?? personaLabel}`}
        subtitle="Every ad in the selection, by spend. Translucent = under the maturity gate (shown, but excluded from the verdict). Click a card for full detail."
        testId="section-persona-ads"
        headerRight={
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ads…"
              className="h-7 px-3 rounded-full border bg-white dark:bg-card text-xs w-40"
              data-testid="input-persona-ad-search"
            />
          </div>
        }
      >
        {verdict.angleSplits.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-4">
            <Pill active={angleFilter === null} onClick={() => setAngleFilter(null)} testId="button-angle-filter-all">All angles</Pill>
            {verdict.angleSplits.map((s) => (
              <Pill
                key={s.angle}
                active={angleFilter === s.angle}
                onClick={() => setAngleFilter(angleFilter === s.angle ? null : s.angle)}
                testId={`button-angle-filter-${s.angle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="max-w-[220px] truncate"
              >
                {s.angle} ({s.ads.length})
              </Pill>
            ))}
          </div>
        )}
        {listAds.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ads match the current filters.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {listAds.map((a) => {
              const mature = isMatureAd(a, minSpend, minImpr);
              const cpa = a.atc > 0 ? a.spend / a.atc : null;
              const ctr = a.impressions > 0 ? (a.clicks / a.impressions) * 100 : null;
              const adLead = leadSourceField ? leadGenAggregate(aggregateAds([a]), leadSourceField) : null;
              const angle = angleOf(a);
              return (
                <div
                  key={a.adId}
                  className={`rounded-xl border overflow-hidden cursor-pointer bg-white dark:bg-card transition-shadow hover:shadow-md ${mature ? "" : "opacity-45"}`}
                  onClick={() => onSelect(a)}
                  data-testid={`card-persona-ad-${a.adId}`}
                >
                  <AdThumb ad={a} />
                  <div className="p-2.5 space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] flex-wrap">
                      {angle !== UNKNOWN_ANGLE && (
                        <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300 font-medium truncate max-w-[90px]" title={angle}>{angle}</span>
                      )}
                      {a.batch && <span className="px-1.5 py-0.5 rounded-full bg-neutral-100 dark:bg-muted text-muted-foreground font-medium">{a.batch}</span>}
                      {!mature && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 font-medium">low data</span>}
                    </div>
                    <div className="text-[11px] font-medium truncate" title={a.adName}>{a.adName}</div>
                    {adLead ? (
                      <>
                        <div className="text-[10px] font-semibold tabular-nums">
                          {fmtInt(adLead.leads)} leads · CPL {fmtUsd(adLead.cpl)}
                        </div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          Click → lead {fmtPct(adLead.clickToLead, 1)} · CPC {fmtUsd(adLead.cpc)} · {fmtUsd(a.spend, 0)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          {fmtUsd(a.spend, 0)}{ctr !== null ? ` · CTR ${ctr.toFixed(2)}%` : ""} · {cpa !== null ? `${fmtUsd(cpa, 0)} / add to cart` : "no ATC"}
                        </div>
                        <div className="text-[10px] text-muted-foreground tabular-nums">
                          {fmtInt(a.purchases)} purch · {fmtUsd(a.revenue, 0)} rev
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
